export type SocureFindingLevel = 'success' | 'warning' | 'info';
export type SocureFinding = {
  code: string;
  level: SocureFindingLevel;
  description: string;
  notes?: string;
};

// A lot of the codes are shared between Socure modules
// Most code descriptions and notes have adjusted wording for brevity or to avoid confusion
// All codes have been assigned a .level property indicating their severity
// Some of the codes must be enabled in Oscilar/Socure before they become available
//
// Original source:
// https://developer.socure.com/docs/advanced-concepts/reason-codes/
// Did not include deprecated codes:
// https://developer.socure.com/docs/advanced-concepts/reason-codes/deprecated-reason-codes/
const codes = {
  I121: {
    code: 'I121',
    level: 'success',
    description: 'Social networks match',
  },
  I127: {
    code: 'I127',
    level: 'success',
    description:
      'Online sources are representative of a broader digital footprint than just social networks',
  },
  I140: {
    code: 'I140',
    level: 'success',
    description: 'Email address tagged as positive',
  },
  I141: {
    code: 'I141',
    level: 'success',
    description: 'Email domain tagged as positive',
  },
  I142: {
    code: 'I142',
    level: 'success',
    description: 'Phone number tagged as positive',
  },
  I143: {
    code: 'I143',
    level: 'success',
    description: 'IP Address tagged as positive',
  },
  I144: {
    code: 'I144',
    level: 'success',
    description: 'Full name tagged as positive',
  },
  I145: {
    code: 'I145',
    level: 'success',
    description: 'SSN tagged as positive',
  },
  I146: {
    code: 'I146',
    level: 'success',
    description: 'Device tagged as positive',
  },
  I196: {
    code: 'I196',
    level: 'success',
    description: 'Global Watchlist search did not return any hits',
  },
  I202: {
    code: 'I202',
    level: 'warning',
    description: 'SSN provided was found to be an ITIN',
  },
  I203: {
    code: 'I203',
    level: 'info',
    description: 'SSN was first seen in credit header records 5-10 years ago',
  },
  I204: {
    code: 'I204',
    level: 'info',
    description:
      'SSN was first seen in credit header records more than 10 years ago',
  },
  I205: {
    code: 'I205',
    level: 'info',
    description: 'SSN is found to be credit inquired once every 2-3 months',
  },
  I206: {
    code: 'I206',
    level: 'info',
    description: 'SSN is found to be credit inquired once every 3-6 months',
  },
  I207: {
    code: 'I207',
    level: 'info',
    description: 'SSN is found to be credit inquired once every 6-12 months',
  },
  I208: {
    code: 'I208',
    level: 'info',
    description: 'SSN is found to be credit inquired less than once a year',
  },
  I209: {
    code: 'I209',
    level: 'success',
    description: 'Full name and DOB combination found in property records',
  },
  I210: {
    code: 'I210',
    level: 'success',
    description:
      'Full name and DOB combination found in education/university records',
  },
  I211: {
    code: 'I211',
    level: 'success',
    description: "Full name and DOB matches a driver's license record",
  },
  I212: {
    code: 'I212',
    level: 'info',
    description: 'SSN is associated with multiple addresses',
  },
  I213: {
    code: 'I213',
    level: 'warning',
    description: 'SSN seems to have a 1-digit error',
  },
  I214: {
    code: 'I214',
    level: 'warning',
    description: 'SSN seems to have a 1-digit typo',
  },
  I215: {
    code: 'I215',
    level: 'warning',
    description: 'Birth year seems to have a 1-digit error',
  },
  I301: {
    code: 'I301',
    level: 'success',
    description: 'Account is correlated with the first name',
  },
  I302: {
    code: 'I302',
    level: 'success',
    description: 'Account is conditionally correlated with the first name',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I304: {
    code: 'I304',
    level: 'success',
    description: 'Account is correlated with the last name',
  },
  I305: {
    code: 'I305',
    level: 'success',
    description: 'Account is conditionally correlated with the last name',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I307: {
    code: 'I307',
    level: 'success',
    description: 'Account is correlated with the business name',
  },
  I308: {
    code: 'I308',
    level: 'success',
    description: 'Account is conditionally correlated with the business name',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I310: {
    code: 'I310',
    level: 'success',
    description: 'Account is correlated with the DOB',
  },
  I311: {
    code: 'I311',
    level: 'success',
    description: 'Account is conditionally correlated with the DOB',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I313: {
    code: 'I313',
    level: 'success',
    description: 'Account is correlated with the SSN',
  },
  I314: {
    code: 'I314',
    level: 'success',
    description: 'Account is conditionally correlated with the SSN',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I316: {
    code: 'I316',
    level: 'success',
    description: 'Account is correlated with the address',
  },
  I317: {
    code: 'I317',
    level: 'success',
    description: 'Account is conditionally correlated with the address',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I319: {
    code: 'I319',
    level: 'success',
    description: 'Account is correlated with the phone',
  },
  I320: {
    code: 'I320',
    level: 'success',
    description: 'Account is conditionally correlated with the phone',
    notes: 'Provided PII is fuzzy-matching with account holder PII.',
  },
  I325: {
    code: 'I325',
    level: 'info',
    description: 'SSN derived from ID Graph',
  },
  I326: {
    code: 'I326',
    level: 'info',
    description: 'DOB derived from ID Graph',
  },
  I328: {
    code: 'I328',
    level: 'info',
    description: 'Account has been seen in last 15 days',
  },
  I329: {
    code: 'I329',
    level: 'info',
    description: 'Account has been seen in last 15-30 days',
  },
  I330: {
    code: 'I330',
    level: 'info',
    description: 'Account has been seen in last 30-45 days',
  },
  I331: {
    code: 'I331',
    level: 'info',
    description: 'Account has been seen in last 45-60 days',
  },
  I332: {
    code: 'I332',
    level: 'info',
    description: 'Account has been seen in last 60-75 days',
  },
  I333: {
    code: 'I333',
    level: 'info',
    description: 'Account has been seen in last 75-90 days',
  },
  I334: {
    code: 'I334',
    level: 'info',
    description: 'Account has been seen in last 90-120 days',
  },
  I335: {
    code: 'I335',
    level: 'info',
    description: 'Account has been seen in last 120-150 days',
  },
  I336: {
    code: 'I336',
    level: 'info',
    description: 'Account has been seen in last 150-180 days',
  },
  I337: {
    code: 'I337',
    level: 'info',
    description: 'Account has been seen in last 180-210 days',
  },
  I338: {
    code: 'I338',
    level: 'info',
    description: 'Account has been seen in last 210-240 days',
  },
  I339: {
    code: 'I339',
    level: 'info',
    description: 'Account has been seen in last 240-270 days',
  },
  I341: {
    code: 'I341',
    level: 'info',
    description: 'Account has been seen in last 270-300 days',
  },
  I342: {
    code: 'I342',
    level: 'info',
    description: 'Account has been seen in last 300-365 days',
  },
  I343: {
    code: 'I343',
    level: 'info',
    description: 'Account has been seen in last 365-730 days',
  },
  I344: {
    code: 'I344',
    level: 'info',
    description: 'Account has been seen over 730 days ago',
  },
  I350: {
    code: 'I350',
    level: 'info',
    description: 'DOB indicates an age between 18 and 20',
  },
  I351: {
    code: 'I351',
    level: 'info',
    description: 'DOB indicates an age between 21 and 24',
  },
  I352: {
    code: 'I352',
    level: 'info',
    description: 'DOB indicates an age over 25',
  },
  I401: {
    code: 'I401',
    level: 'info',
    description: 'Device token previously encountered',
  },
  I402: {
    code: 'I402',
    level: 'info',
    description: 'Multiple users associated with device',
  },
  I403: {
    code: 'I403',
    level: 'info',
    description: 'Identity may not be primary device user',
  },
  I404: {
    code: 'I404',
    level: 'success',
    description: 'Identity associated with primary device owner',
  },
  I405: {
    code: 'I405',
    level: 'info',
    description: 'Device token previously associated with phone number',
  },
  I406: {
    code: 'I406',
    level: 'info',
    description: 'Device token previously associated with email address',
  },
  I407: {
    code: 'I407',
    level: 'info',
    description: 'Device token previously associated with first name',
  },
  I408: {
    code: 'I408',
    level: 'info',
    description: 'Device token previously associated with last name',
  },
  I409: {
    code: 'I409',
    level: 'info',
    description: 'Device token not encountered within the past 5 days',
  },
  I410: {
    code: 'I410',
    level: 'info',
    description: 'Device session is associated with more than 2 phone numbers',
  },
  I411: {
    code: 'I411',
    level: 'info',
    description: 'Device session is associated with more than 2 emails',
  },
  I412: {
    code: 'I412',
    level: 'info',
    description: 'Device session is associated with more than 2 first names',
  },
  I413: {
    code: 'I413',
    level: 'info',
    description: 'Device session is associated with more than 2 last names',
  },
  I414: {
    code: 'I414',
    level: 'warning',
    description: 'Session connected using a proxy',
  },
  I415: {
    code: 'I415',
    level: 'warning',
    description:
      'Session connected using an IP located more than 100 miles from the address',
  },
  I416: {
    code: 'I416',
    level: 'success',
    description: 'Session IP originates from within the United States',
  },
  I417: {
    code: 'I417',
    level: 'warning',
    description: 'Session IP originates from outside the United States',
  },
  I419: {
    code: 'I419',
    level: 'info',
    description: 'Session connected using a residential IP address',
  },
  I420: {
    code: 'I420',
    level: 'info',
    description: 'Session connected using a commercial IP address',
  },
  I421: {
    code: 'I421',
    level: 'info',
    description: 'Session connected using a mobile IP address',
  },
  I423: {
    code: 'I423',
    level: 'info',
    description: 'Device is a physical device',
  },
  I424: {
    code: 'I424',
    level: 'info',
    description: 'Device type is a desktop or laptop computer',
  },
  I425: {
    code: 'I425',
    level: 'info',
    description: 'Device type is mobile, tablet or wearable',
  },
  I428: {
    code: 'I428',
    level: 'warning',
    description:
      'Mobile network associated with the device is a non-US carrier',
  },
  I429: {
    code: 'I429',
    level: 'success',
    description:
      'IP address associated with the request comes from US or a US territory',
  },
  I430: {
    code: 'I430',
    level: 'warning',
    description:
      'IP address associated with the request does not come from US or a US territory',
  },
  I431: {
    code: 'I431',
    level: 'success',
    description:
      'Timezone set on the device indicates that the device is in the US',
  },
  I520: {
    code: 'I520',
    level: 'success',
    description: 'Email address is considered low risk',
    // notes: "The emailRisk score is less than 0.9."
  },
  I550: {
    code: 'I550',
    level: 'info',
    description: 'Email address is approx 180-365 days old',
    notes:
      'Email address was first seen on a verifiable public network source between 180 and 365 days prior to the date of this check.',
  },
  I551: {
    code: 'I551',
    level: 'info',
    description: 'Email address is approx  1-2 years old',
    notes:
      'Email address was first seen on a verifiable public network source between 365 and 730 days prior to the date of this check.',
  },
  I553: {
    code: 'I553',
    level: 'success',
    description: 'Email address is more than 2 years old',
    notes:
      'Email address was first seen on a verifiable public network source greater than 730 days prior to the date of this check. This is generally indicative of a low-risk email as people tend to hold the same email address for >5 years.',
  },
  I554: {
    code: 'I554',
    level: 'success',
    description: 'High-confidence match between the email and last name',
  },
  I555: {
    code: 'I555',
    level: 'success',
    description: 'Email address domain is more than 180 days old',
  },
  I556: {
    code: 'I556',
    level: 'success',
    description: 'Email address can be resolved to the individual',
  },
  I557: {
    code: 'I557',
    level: 'success',
    description: 'Email is correlated with the first name',
  },
  I558: {
    code: 'I558',
    level: 'success',
    description: 'Email is correlated with the last name',
  },
  I559: {
    code: 'I559',
    level: 'success',
    description: 'High-confidence match between the email and first name',
  },
  I560: {
    code: 'I560',
    level: 'info',
    description: 'Email address domain represents a Fortune 500 company',
  },
  I561: {
    code: 'I561',
    level: 'info',
    description: 'Email address domain represents a US College/University',
  },
  I562: {
    code: 'I562',
    level: 'info',
    description: 'Email username contains ZIP code of current application',
  },
  I563: {
    code: 'I563',
    level: 'warning',
    description:
      'Email address domain contains a special use domain (RFC, arpa, example, invalid, local, onion, etc.)',
  },
  I564: {
    code: 'I564',
    level: 'info',
    description:
      'Special characters which are not periods or underscores found in email alias',
  },
  I565: {
    code: 'I565',
    level: 'warning',
    description:
      'Email username contains a role-level alias (employees, support, info, postmaster, admin, user, noreply, etc.)',
    notes:
      'Email username contains one or more of the following aliases: any, all, everyone, no-one, no-reply, noreply, none, employees, empty, no-email, noemail, user, account, daemon, admin, user, support, postmaster, info, sales, services, faq, questions.',
  },
  I566: {
    code: 'I566',
    level: 'info',
    description: 'Email address provider is found in Public Web Source',
    notes:
      'Email address originates from a free public email address provider.',
  },
  I567: {
    code: 'I567',
    level: 'info',
    description: 'Email username contains five or more alphabetic sections',
    notes:
      'An alphabetic section is a series of letters separated by either numbers or special characters prior to the @ sign in an email address.',
  },
  I568: {
    code: 'I568',
    level: 'info',
    description: 'Email handle contains a name',
  },
  I569: {
    code: 'I569',
    level: 'success',
    description: "Email handle contains applicant's first name",
  },
  I570: {
    code: 'I570',
    level: 'success',
    description: "Email handle contains applicant's last name",
  },
  I571: {
    code: 'I571',
    level: 'info',
    description: 'Email top-level domain represents a non-US country',
  },
  I572: {
    code: 'I572',
    level: 'warning',
    description: 'Email sub domain possibly a typo of a popular email domain',
  },
  I573: {
    code: 'I573',
    level: 'warning',
    description: 'Email top level domain possibly a typo of a popular TLD',
  },
  I574: {
    code: 'I574',
    level: 'info',
    description: 'Email domain is Apple private relay',
  },
  I575: {
    code: 'I575',
    level: 'warning',
    description: 'Provided email is possible filler email',
  },
  I576: {
    code: 'I576',
    level: 'success',
    description: 'Email handle aligns to the pattern of valid email addresses',
    notes:
      'Upon analyzing patterns generated from the email handle, it is observed that the email matches the usual format of email addresses typical of non-fraudulent users.',
  },
  I577: {
    code: 'I577',
    level: 'info',
    description: 'Email is associated to small businesses',
  },
  I578: {
    code: 'I578',
    level: 'info',
    description: 'Email belongs to a corporate',
  },
  I601: {
    code: 'I601',
    level: 'info',
    description: 'Phone number is a landline',
  },
  I602: {
    code: 'I602',
    level: 'info',
    description: 'Phone number is a mobile line',
  },
  I605: {
    code: 'I605',
    level: 'info',
    description: 'Phone number is a Premium-rate line',
  },
  I608: {
    code: 'I608',
    level: 'info',
    description: 'Phone number is commercial or dual-purpose',
  },
  I609: {
    code: 'I609',
    level: 'info',
    description: 'Phone number is consumer or residential',
  },
  I610: {
    code: 'I610',
    level: 'success',
    description: 'Phone number is correlated with the address',
  },
  I611: {
    code: 'I611',
    level: 'success',
    description: 'Phone number is associated with a major carrier',
  },
  I614: {
    code: 'I614',
    level: 'success',
    description: 'Phone number has been in service for more than 365 days',
  },
  I616: {
    code: 'I616',
    level: 'warning',
    description:
      'Phone number is associated with a Mobile Virtual Network Operator',
  },
  I618: {
    code: 'I618',
    level: 'success',
    description: 'Phone number can be resolved to the individual',
  },
  I620: {
    code: 'I620',
    level: 'success',
    description: 'Phone number is considered low risk',
    // notes: "The phoneRisk score is less than 0.9."
  },
  I621: {
    code: 'I621',
    level: 'success',
    description: 'Phone is correlated with first name',
  },
  I622: {
    code: 'I622',
    level: 'success',
    description: 'Phone is correlated with last name',
  },
  I623: {
    code: 'I623',
    level: 'success',
    description: 'High-confidence match between the phone and first name',
  },
  I624: {
    code: 'I624',
    level: 'success',
    description: 'High-confidence match between the phone and last name',
  },
  I625: {
    code: 'I625',
    level: 'success',
    description: 'Phone number has never been ported',
  },
  I626: {
    code: 'I626',
    level: 'info',
    description: 'Phone number was ported at least 60 days ago',
  },
  I627: {
    code: 'I627',
    level: 'success',
    description: 'Phone and SSN are linked',
  },
  I630: {
    code: 'I630',
    level: 'success',
    description:
      'Phone subscriber correlated with phone number for more than 365 days',
  },
  I631: {
    code: 'I631',
    level: 'info',
    description: 'IP address is provided by a mobile carrier',
  },
  I632: {
    code: 'I632',
    level: 'info',
    description: 'IP Connection is consumer',
  },
  I633: {
    code: 'I633',
    level: 'info',
    description: 'IP Connection is business',
  },
  I634: {
    code: 'I634',
    level: 'info',
    description: 'Proxy is an educational institution',
  },
  I635: {
    code: 'I635',
    level: 'info',
    description: 'Proxy is registered to a corporation',
  },
  I636: {
    code: 'I636',
    level: 'success',
    description: 'IP address originates from the US or US territories',
  },
  I637: {
    code: 'I637',
    level: 'success',
    description: 'Email address is associated with the phone number',
  },
  I638: {
    code: 'I638',
    level: 'info',
    description:
      'IP proxy is originating from Google or Apple consumer privacy networks',
    notes:
      "IP is routed through either Apple or Google privacy network proxies, such as Apple Private Relay, Google One VPN, and Google Fi VPN. These networks are considered to be less risky due to the nature of usage and the control exerted by the services on the IP's geographic location.",
  },
  I639: {
    code: 'I639',
    level: 'info',
    description: 'IP address is a consumer privacy network proxy',
    notes:
      'A consumer privacy network proxy service controls the geographic location of the egress proxy (i.e. exit IP address) through which the end user is routed, ensuring that the users cannot "region hop". These locations are typically close to the user\'s actual geographic location. Examples include Apple Private Relay, Google One VPN, and Google Fi VPN.',
  },
  I640: {
    code: 'I640',
    level: 'warning',
    description: 'Phone number might have been mistyped',
  },
  I641: {
    code: 'I641',
    level: 'success',
    description: 'Presented identity shows high overall correlation', //  in Socure's network
  },
  I699: {
    code: 'I699',
    level: 'success',
    description:
      'No SIM Swap was detected within the specified timeframe for the provided phone number',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  I704: {
    code: 'I704',
    level: 'info',
    description: 'Address is multi-unit or high rise',
  },
  I705: {
    code: 'I705',
    level: 'info',
    description: 'Address is single unit',
  },
  I706: {
    code: 'I706',
    level: 'info',
    description: 'Address is an accredited college or university',
  },
  I707: {
    code: 'I707',
    level: 'info',
    description: 'Address is residential',
  },
  I708: {
    code: 'I708',
    level: 'success',
    description: 'Address can be resolved to the individual',
  },
  I709: {
    code: 'I709',
    level: 'success',
    description: 'Address is correlated with first name',
  },
  I710: {
    code: 'I710',
    level: 'success',
    description: 'Address is correlated with last name',
  },
  I711: {
    code: 'I711',
    level: 'success',
    description: 'Address is confirmed as deliverable',
  },
  I712: {
    code: 'I712',
    level: 'info',
    description:
      'Address is confirmed deliverable by dropping secondary information (apartment, unit, etc.)',
  },
  I713: {
    code: 'I713',
    level: 'info',
    description:
      'Address is confirmed deliverable but was missing secondary information (apartment, unit, etc.)',
  },
  I714: {
    code: 'I714',
    level: 'info',
    description:
      "Address is valid but doesn't currently receive US Postal Service street delivery",
  },
  I715: {
    code: 'I715',
    level: 'info',
    description: 'Address is correlated with a past address',
  },
  I716: {
    code: 'I716',
    level: 'info',
    description: 'Address is a Small Office/Home Office (SOHO)',
    notes:
      'A Small Office/Home Office (SOHO) refers to a small business that is located in a small office space or residence.',
  },
  I718: {
    code: 'I718',
    level: 'success',
    description: 'High-confidence match between the address and first name',
  },
  I719: {
    code: 'I719',
    level: 'success',
    description: 'High-confidence match between the address and last name',
  },
  I720: {
    code: 'I720',
    level: 'success',
    description: 'Address is considered low risk',
    // notes: "The addressRisk score is less than 0.9.Note: Scores between 0.9 and 0.97 will not trigger as either high risk or low risk."
  },
  I721: {
    code: 'I721',
    level: 'success',
    description:
      'Email address is correlated with physical address (street-level match)',
  },
  I722: {
    code: 'I722',
    level: 'success',
    description:
      'Email address is partially correlated with physical address (city/state match)',
  },
  I808: {
    code: 'I808',
    level: 'warning',
    description: 'Document image resolution is insufficient',
  },
  I820: {
    code: 'I820',
    level: 'success',
    description: 'Headshot passes integrity checks',
  },
  I822: {
    code: 'I822',
    level: 'success',
    description: 'First name matches document',
  },
  I823: {
    code: 'I823',
    level: 'success',
    description: 'Last name matches document',
  },
  I824: {
    code: 'I824',
    level: 'success',
    description: 'Address matches document',
  },
  I825: {
    code: 'I825',
    level: 'success',
    description: 'DOB matches document',
  },
  I826: {
    code: 'I826',
    level: 'success',
    description: 'Document number matches uploaded document',
  },
  I831: {
    code: 'I831',
    level: 'info',
    description: 'Minimum required information extracted from document Barcode',
  },
  I833: {
    code: 'I833',
    level: 'info',
    description: 'Minimum required information extracted from document MRZ',
  },
  I834: {
    code: 'I834',
    level: 'success',
    description: 'Self-portrait passes liveness checks',
  },
  I836: {
    code: 'I836',
    level: 'success',
    description: 'Document image correlates with self-portrait',
  },
  I837: {
    code: 'I837',
    level: 'info',
    description: 'Self-portrait was not considered in decision',
  },
  I838: {
    code: 'I838',
    level: 'info',
    description: 'Minimum required Information extracted from the document',
  },
  I845: {
    code: 'I845',
    level: 'info',
    description: 'Age meets minimum requirements',
  },
  I846: {
    code: 'I846',
    level: 'info',
    description: 'Expired document within the grace period specified',
  },
  I847: {
    code: 'I847',
    level: 'info',
    description: 'Document about to expire within the future grace period',
  },
  I848: {
    code: 'I848',
    level: 'warning',
    description: 'Document is not captured properly',
  },
  I849: {
    code: 'I849',
    level: 'warning',
    description: 'Incorrect ID type selected',
  },
  I854: {
    code: 'I854',
    level: 'warning',
    description:
      'Back of the license was not passed; no barcode to extract information',
  },
  I855: {
    code: 'I855',
    level: 'info',
    description: 'First Name matches with a known Nick Name or Alias',
  },
  I856: {
    code: 'I856',
    level: 'info',
    description: 'Liveness was not determined',
  },
  I859: {
    code: 'I859',
    level: 'success',
    description: 'Image validated against past headshots',
    notes: 'Image Alert List did not return any hits.',
  },
  I860: {
    code: 'I860',
    level: 'info',
    description: 'Facial correlation was not calculated',
  },
  I900: {
    code: 'I900',
    level: 'success',
    description: 'DOB day matches resolved identity',
  },
  I901: {
    code: 'I901',
    level: 'success',
    description: 'DOB month matches resolved identity',
  },
  I902: {
    code: 'I902',
    level: 'success',
    description: 'DOB year matches resolved identity',
  },
  I903: {
    code: 'I903',
    level: 'warning',
    description: 'Address was not provided at input',
  },
  I904: {
    code: 'I904',
    level: 'warning',
    description: 'SSN was not provided at input',
  },
  I905: {
    code: 'I905',
    level: 'warning',
    description: 'SSN provided at input contained only 4 digits',
  },
  I906: {
    code: 'I906',
    level: 'warning',
    description: 'DOB was not provided at input',
  },
  I907: {
    code: 'I907',
    level: 'warning',
    description: 'SSN is issued to a non-US citizen',
    notes:
      'SSN belongs to a non-US citizen who is lawfully present in the United States. This individual is a legal US resident, but may belong to one of the following categories: Temporary residents, Permanent residents, Migrants (such as refugees or asylees)',
  },
  I908: {
    code: 'I908',
    level: 'warning',
    description: 'Address is correlated with a military ZIP code',
    notes:
      'Address street, city, and state or street and ZIP code belong to a ZIP code designated for military units and diplomatic organizations, often in foreign locations.',
  },
  I909: {
    code: 'I909',
    level: 'warning',
    description: 'First name and last name are possibly reversed',
  },
  I910: {
    code: 'I910',
    level: 'info',
    description: 'Address is correlated with a past address',
    notes:
      'Street address (not including city, state, and ZIP code) is correlated with a historical record and may not be current.',
  },
  I911: {
    code: 'I911',
    level: 'warning',
    description: 'Address is a PO Box',
  },
  I912: {
    code: 'I912',
    level: 'info',
    description: 'SSN was randomly-issued by the SSA',
    notes:
      "SSN matches one of the Social Security Administration's (SSA) ~420 million randomly assigned SSNs that were not yet assigned as of June 25, 2011. The SSA began issuing randomized SSNs on June 25, 2011, which affected the SSN assignment process in the following ways: It eliminated the geographical significance of the first three digits of the SSN (referred to as the area number), which no longer reflects the assignee's state (as determined by the ZIP code of the mailing address). It eliminated the significance of the highest group number, and as a result, the High Group List is frozen in time and can only be used to see the area and group numbers SSA issued prior to the randomization implementation date. Previously unassigned area numbers were introduced for assignment excluding area numbers 000, 666, and 900-999.",
  },
  I913: {
    code: 'I913',
    level: 'success',
    description: 'SSN is not reported as deceased',
  },
  I914: {
    code: 'I914',
    level: 'success',
    description: 'Identity is not reported as deceased',
  },
  I917: {
    code: 'I917',
    level: 'warning',
    description:
      'Full name and address can be resolved to the individual, but the SSN cannot',
  },
  I919: {
    code: 'I919',
    level: 'success',
    description:
      'Full name, address, and SSN can be resolved to the individual',
  },
  I920: {
    code: 'I920',
    level: 'info',
    description: 'Emerging identity',
    notes:
      'Identity is too new to determine if it is fraudulent or not. It could be synthetic, or also a new identity (“thin file”).',
  },
  I921: {
    code: 'I921',
    level: 'info',
    description: 'Address is an accredited college or university',
  },
  I922: {
    code: 'I922',
    level: 'warning',
    description: 'Mobile number was not provided at input',
  },
  I923: {
    code: 'I923',
    level: 'warning',
    description: 'Email address was not provided at input',
  },
  I930: {
    code: 'I930',
    level: 'info',
    description:
      'Name and DOB of the identity matches a notable personality/celebrity',
  },
  I931: {
    code: 'I931',
    level: 'warning',
    description: 'First name or last name may refer to a non-person entity',
    notes:
      'Name may be associated by exact matching with an entity that is not human, such as an organization or business. Common examples include LLC., Services, Market, Style, Spice, Beauty, and Salon.',
  },
  I932: {
    code: 'I932',
    level: 'info',
    description:
      'Name and DOB of the identity match a notable individual in the sports industry',
    notes:
      "Identity potentially matches a current or former athlete, coach, or other prominent figure associated with various college and professional sports organizations, based on publicly available sources. List is not exhaustive and is continuously updated. This warning should not be relied upon as the sole indicator of an individual's identity or eligibility. False positives may occur, and additional verification steps may be necessary",
    // notes: "Identity matches a current or former athlete, coach, or other prominent figure associated with various college and professional sports organizations, based on publicly available sources. This code is not exhaustive and is continuously updated. I932 serves as as an additional risk signal to maintain the integrity of gaming, and prevent insider betting or potential conflicts of interest. It is not a standard part of CIP practices and should not be relied upon as the sole indicator of an individual's identity or eligibility. To ensure regulatory compliance and effectively incorporate I932 into risk assessment processes, businesses should consult their compliance teams. False positives may occur, and additional verification steps may be necessary. The presence of an I932 match does not necessarily imply any wrongdoing or prohibited activity."
  },
  I933: {
    code: 'I933',
    level: 'warning',
    description: 'Multi-party application detected',
    notes: 'AND/OR operator detected in name. For example: James and Tony',
  },
  I934: {
    code: 'I934',
    level: 'info',
    description:
      'Name and DOB of the identity matches an individual formerly in the sports industry',
  },
  I990: {
    code: 'I990',
    level: 'info',
    description: 'Emergent address or recent move',
    notes:
      'Address provided is not an exact match, but is found in close proximity. Individual may have recently relocated.',
  },
  I998: {
    code: 'I998',
    level: 'success',
    description: 'Name, SSN and DOB correlation verified by SSA',
    notes:
      'Identity matches SSA records under all of the following conditions: First initial matches the first name on record. For example, Elizabeth will match Elijah but nicknames like Betsy are disallowed. Last name is within one edit distance of the last name on record. For example, Denton will match Fenton. SSN matched exactly. Note that ITINs cannot be validated by the SSA. Additionally, the DOB can be: An exact match on YYYY. For example, 1971/03/22 will match any date from 1971/01/01 through 1971/12/31. Within a one year radius if there is an exact match on MM. For example, 1971/03/22 will match any date from 1970/03/23 through 1970/03/31, and 1972/03/01 through 1972/03/21.',
  },
  I999: {
    code: 'I999',
    level: 'info',
    description: 'Identity decease unverified by SSA',
    notes:
      'SSA has not confirmed that identity is deceased at the federal level.',
  },
  I1001: {
    code: 'I1001',
    level: 'info',
    description: 'Identity not found within Consortium Institutions',
  },
  I1002: {
    code: 'I1002',
    level: 'warning',
    description:
      'SSN was not provided and prefill was not available for the given identity',
  },
  I1003: {
    code: 'I1003',
    level: 'info',
    description:
      'SSN was not provided but prefill was available for the given identity',
  },
  I1004: {
    code: 'I1004',
    level: 'info',
    description:
      'Matched identity onto at least 1 other Consortium Institution',
  },
  I1005: {
    code: 'I1005',
    level: 'info',
    description:
      'Matched identity onto at least 4 other Consortium Institutions',
  },
  R110: {
    code: 'R110',
    level: 'warning',
    description: 'Email associated with fraud on the Alert List',
  },
  R111: {
    code: 'R111',
    level: 'warning',
    description: 'SSN associated with fraud on the Alert List',
  },
  R113: {
    code: 'R113',
    level: 'warning',
    description: 'Phone number associated with fraud on the Alert List',
  },
  R140: {
    code: 'R140',
    level: 'warning',
    description: 'Email address tagged as negative',
  },
  R141: {
    code: 'R141',
    level: 'warning',
    description: 'Email domain tagged as negative',
  },
  R142: {
    code: 'R142',
    level: 'warning',
    description: 'Phone number tagged as negative',
  },
  R143: {
    code: 'R143',
    level: 'warning',
    description: 'IP Address tagged as negative',
  },
  R144: {
    code: 'R144',
    level: 'warning',
    description: 'Full name tagged as negative',
  },
  R145: {
    code: 'R145',
    level: 'warning',
    description: 'SSN tagged as negative',
  },
  R146: {
    code: 'R146',
    level: 'warning',
    description: 'Device tagged as negative',
  },
  R150: {
    code: 'R150',
    level: 'info',
    description: 'Unable to call negative / positive service',
  },
  R160: {
    code: 'R160',
    level: 'warning',
    description: 'Email address tagged as suspect',
  },
  R161: {
    code: 'R161',
    level: 'warning',
    description: 'Email domain tagged as suspect',
  },
  R162: {
    code: 'R162',
    level: 'warning',
    description: 'Phone number tagged as suspect',
  },
  R163: {
    code: 'R163',
    level: 'warning',
    description: 'IP Address tagged as suspect',
  },
  R164: {
    code: 'R164',
    level: 'warning',
    description: 'Full name tagged as suspect',
  },
  R165: {
    code: 'R165',
    level: 'warning',
    description: 'SSN tagged as suspect',
  },
  R166: {
    code: 'R166',
    level: 'warning',
    description: 'Device tagged as suspect',
  },
  R171: {
    code: 'R171',
    level: 'warning',
    description:
      'Mobile number originates from a high-risk OFAC sanctioned country',
  },
  R172: {
    code: 'R172',
    level: 'warning',
    description:
      'Email likely originates from a high-risk OFAC sanctioned country',
  },
  R173: {
    code: 'R173',
    level: 'warning',
    description: 'Document originates from a high-risk OFAC sanctioned country',
  },
  R180: {
    code: 'R180',
    level: 'warning',
    description: 'Watchlist search returned at least one hit on OFAC SDN list',
  },
  R181: {
    code: 'R181',
    level: 'warning',
    description:
      'Watchlist search returned at least one hit on OFAC Non-SDN lists',
  },
  R182: {
    code: 'R182',
    level: 'warning',
    description:
      'Watchlist search returned at least one hit on a sanction list excluding any OFAC hits',
  },
  R183: {
    code: 'R183',
    level: 'warning',
    description:
      'Watchlist search returned at least one hit on an enforcement list excluding any OFAC hits',
  },
  R184: {
    code: 'R184',
    level: 'warning',
    description:
      'Watchlist search returned at least one hit on a politically exposed person',
  },
  R185: {
    code: 'R185',
    level: 'warning',
    description:
      'Watchlist search returned at least one entity that had adverse media articles',
  },
  R186: {
    code: 'R186',
    level: 'warning',
    description: 'Global Watchlist search returned at least one hit',
    notes:
      'Information submitted appears in one of the following lists: sanction, enforcement, PEP, or adverse media',
    // This reason code is mutually exclusive to I196
  },
  R187: {
    code: 'R187',
    level: 'warning',
    description: 'Address resolves to a High-Intensity Drug Trafficking Area',
    notes:
      'Address submitted matches the geolocation contained on the High Intensity Drug Trafficking Areas (HIDTA) source list. HIDTA program was created by Congress with the Anti-Drug Abuse Act of 1988, provides assistance to Federal, state, local, and tribal law enforcement agencies operating in areas determined to be critical drug-trafficking regions of the United States.',
  },
  R188: {
    code: 'R188',
    level: 'warning',
    description: 'Address resolves to a High-Intensity Financial Crime Area',
    notes:
      'Address submitted matches the geolocation contained on the High Intensity Financial Crime Areas (HIFCA) source list. This list contains geographic locations that present a particularly high risk of money laundering and financial crime.',
  },
  R189: {
    code: 'R189',
    level: 'warning',
    description:
      'Watchlist search returned at least one entity on a custom watchlist',
  },
  R190: {
    code: 'R190',
    level: 'warning',
    description:
      'Watchlist search returned at least one email match on the sanctions list',
  },
  R191: {
    code: 'R191',
    level: 'warning',
    description:
      'Watchlist search returned at least one phone number match on the sanctions list',
  },
  R195: {
    code: 'R195',
    level: 'warning',
    description:
      'Watchlist search returned at least one SSN match on the sanctions list',
  },
  R201: {
    code: 'R201',
    level: 'warning',
    description: 'SSN is invalid',
  },
  R202: {
    code: 'R202',
    level: 'warning',
    description: 'SSN is reported as deceased',
  },
  R203: {
    code: 'R203',
    level: 'warning',
    description: 'SSN is associated with multiple identities',
  },
  R204: {
    code: 'R204',
    level: 'warning',
    description: 'SSN is associated with multiple last names',
  },
  R205: {
    code: 'R205',
    level: 'warning',
    description: 'SSN is associated with a different name and address',
  },
  R206: {
    code: 'R206',
    level: 'warning',
    description: 'SSN was issued prior to the DOB provided',
  },
  R207: {
    code: 'R207',
    level: 'warning',
    description: 'SSN is associated with multiple DOBs',
  },
  R209: {
    code: 'R209',
    level: 'info',
    description: 'No former addresses associated with SSN',
  },
  R210: {
    code: 'R210',
    level: 'warning',
    description: 'SSN usage unusually high',
    notes:
      'Within the last two years this SSN has been used an abnormally high number of times as part of applications involving KYC and represents an increased risk of third-party fraud.',
  },
  R211: {
    code: 'R211',
    level: 'warning',
    description: 'SSN seen in frozen credit files',
    notes:
      'Security freeze was initiated on a credit file containing this SSN and has not yet been lifted.',
  },
  R212: {
    code: 'R212',
    level: 'warning',
    description: 'Last name not associated with SSN',
  },
  R213: {
    code: 'R213',
    level: 'warning',
    description:
      'Full name does not correlate with the name of the resolved individual',
  },
  R214: {
    code: 'R214',
    level: 'warning',
    description: 'SSN does not match the SSN of the resolved identity',
  },
  R215: {
    code: 'R215',
    level: 'warning',
    description: 'First name is not associated with the SSN',
  },
  R216: {
    code: 'R216',
    level: 'warning',
    description:
      'Date of birth does not match the date of birth of the resolved identity',
  },
  R217: {
    code: 'R217',
    level: 'info',
    description: 'SSN was randomly-issued by the SSA',
    notes:
      "SSN matches one of the Social Security Administration's (SSA) ~420 million randomly assigned SSNs that were not yet assigned as of June 25, 2011. The SSA began issuing randomized SSNs on June 25, 2011, which affected the SSN assignment process in the following ways: It eliminated the geographical significance of the first three digits of the SSN (referred to as the area number), which no longer reflects the assignee's state (as determined by the ZIP code of the mailing address). It eliminated the significance of the highest group number, and as a result, the High Group List is frozen in time and can only be used to see the area and group numbers SSA issued prior to the randomization implementation date.Previously unassigned area numbers were introduced for assignment excluding area numbers 000, 666, and 900-999.When only the last 4-digits of a SSN are passed, they will first be resolved to a full 9-digit SSN based on the identity, before evaluating whether the SSN was randomly generated.",
  },
  R218: {
    code: 'R218',
    level: 'warning',
    description: 'SSN is not the primary SSN for the resolved identity',
    notes:
      'Indicates that there are multiple SSNs associated with the identity, out of which the primary SSN resolved does not match with the input SSN',
  },
  R219: {
    code: 'R219',
    level: 'info',
    description: 'SSN not found in public records',
    notes:
      'Indicates that the SSN has not appeared in any public records or other data sources we access. It essentially means that, according to our data, the SSN is new or has not been used widely. This is not necessarily a sign of any issue or suspicious activity. It is common for recent immigrants or individuals who are just starting to build their financial history in the U.S. to have a minimal financial footprint initially',
  },
  R220: {
    code: 'R220',
    level: 'warning',
    description: 'Issue date for the provided SSN could not be resolved',
    notes: 'High uncertainty regarding the issue date for the SSN.',
  },
  R221: {
    code: 'R221',
    level: 'warning',
    description: 'Phone number not associated with any SSN',
    notes:
      'There were no identifiable SSNs on credit records associated with the phone number.',
  },
  R222: {
    code: 'R222',
    level: 'warning',
    description: 'SSN could not be confirmed as trustworthy',
    notes:
      'Non-confirmed SSNs may still be associated with the individual but are less trust-worthy and hence have higher propensity for fraud.',
  },
  R223: {
    code: 'R223',
    level: 'warning',
    description:
      'SSN is not one of the SSNs associated with phone number in last two years',
  },
  R224: {
    code: 'R224',
    level: 'warning',
    description:
      'Phone number has seen an unusually high number of unique SSNs (more than 4) as part of credit inquiry records',
  },
  R225: {
    code: 'R225',
    level: 'warning',
    description:
      'SSN has seen an unusually high number of unique first names (more than 4) as part of credit inquiry records',
  },
  R226: {
    code: 'R226',
    level: 'info',
    description: 'Multiple phones are marked current for the SSN',
  },
  R227: {
    code: 'R227',
    level: 'warning',
    description:
      'SSN credit inquired at an unusually high frequency (more than once a month)',
  },
  R228: {
    code: 'R228',
    level: 'warning',
    description: 'Date of birth barely matches resolved identity (1 part)',
  },
  R229: {
    code: 'R229',
    level: 'warning',
    description: 'Date of birth partially matches resolved identity (2 parts)',
  },
  R230: {
    code: 'R230',
    level: 'info',
    description:
      'SSN was first seen in credit header records within the last 1 year',
  },
  R231: {
    code: 'R231',
    level: 'info',
    description: 'SSN was first seen in credit header records 1-3 years ago',
  },
  R232: {
    code: 'R232',
    level: 'info',
    description: 'SSN was first seen in credit header records 3-5 years ago',
  },
  R233: {
    code: 'R233',
    level: 'info',
    description:
      'No SSN found in credit header records for the given full name and date of birth',
  },
  R234: {
    code: 'R234',
    level: 'warning',
    description:
      'SSN is off by 1 digit from SSNs in credit header data associated with full name and date of birth',
  },
  R235: {
    code: 'R235',
    level: 'warning',
    description:
      'Full name could not be fuzzy-matched with full names associated with SSN in credit inquiry records',
  },
  R236: {
    code: 'R236',
    level: 'warning',
    description:
      'DOB month does not match any DOB month found on credit inquiry records',
  },
  R237: {
    code: 'R237',
    level: 'warning',
    description:
      'SSN is not an exact match to the SSNs associated with the given full name and DOB',
  },
  R238: {
    code: 'R238',
    level: 'warning',
    description:
      'SSN is off by 2 digits from SSNs in credit header data associated with full name and date of birth',
  },
  R239: {
    code: 'R239',
    level: 'warning',
    description:
      'SSN is off by 3 digits from SSNs in credit header data associated with full name and date of birth',
  },
  R240: {
    code: 'R240',
    level: 'warning',
    description:
      'SSN was issued more than 2 years after the resolved birth year',
  },
  R241: {
    code: 'R241',
    level: 'warning',
    description: 'SSN is off by 2 digits from resolved identity',
  },
  R242: {
    code: 'R242',
    level: 'warning',
    description: 'SSN is off by 2 digits from resolved identity',
  },
  R243: {
    code: 'R243',
    level: 'warning',
    description:
      'Individual seems to be changing their date of birth to appear 18 or older',
  },
  R244: {
    code: 'R244',
    level: 'warning',
    description:
      'Individual seems to be changing their date of birth to appear 21 or older',
  },
  R298: {
    code: 'R298',
    level: 'warning',
    description: 'Identity resembles a manipulated Synthetic identity',
    notes:
      'Represents high probability that an identity is manipulated or fabricated due to its PII characteristics. Generated using pattern-based intelligence models, which are driven by findings from extensive fraud investigations into past synthetic applications.',
  },
  R299: {
    code: 'R299',
    level: 'warning',
    description: 'Identity resembles a fabricated Synthetic identity',
    notes:
      'Represents high probability that an identity is manipulated or fabricated due to its PII characteristics. Generated using pattern-based intelligence models, which are driven by findings from extensive fraud investigations into past synthetic applications.',
  },
  R301: {
    code: 'R301',
    level: 'warning',
    description: 'Account is not correlated with the first name',
  },
  R302: {
    code: 'R302',
    level: 'warning',
    description: 'Account is not correlated with the last name',
  },
  R303: {
    code: 'R303',
    level: 'warning',
    description: 'Account is not correlated with the business name',
  },
  R304: {
    code: 'R304',
    level: 'warning',
    description: 'Account is not correlated with the DOB',
  },
  R305: {
    code: 'R305',
    level: 'warning',
    description: 'Account is not correlated with the SSN',
  },
  R306: {
    code: 'R306',
    level: 'warning',
    description: 'Account is not correlated with the address',
  },
  R307: {
    code: 'R307',
    level: 'warning',
    description: 'Account is not correlated with the phone',
  },
  R309: {
    code: 'R309',
    level: 'warning',
    description: 'Provided routing number is invalid',
  },
  R311: {
    code: 'R311',
    level: 'warning',
    description: 'Account warning',
    notes:
      'Account has a negative history and might already be closed or could be closed in the near future. This may be due to a suspicious account number format.',
  },
  R312: {
    code: 'R312',
    level: 'warning',
    description: 'Account information is not available',
  },
  R313: {
    code: 'R313',
    level: 'warning',
    description: 'Account Structure Issue',
  },
  R314: {
    code: 'R314',
    level: 'warning',
    description: 'Deceased signal from ID Graph',
  },
  R315: {
    code: 'R315',
    level: 'warning',
    description: 'Elevated account return risk',
  },
  R350: {
    code: 'R350',
    level: 'warning',
    description:
      'DOB indicates individual is less than 16 or more than 100 years old',
    notes:
      'Age is calculated based on the DOB on record. If there is no DOB on record, age is calculated off of the input DOB.',
  },
  R351: {
    code: 'R351',
    level: 'warning',
    description: 'DOB indicates an age under 13',
    notes:
      "Children's Online Privacy Protection Act (COPPA) requires that online collection of PII for children under the age of 13 must be done through verified parental consent language, and restricts certain marketing and use of this data.",
  },
  R352: {
    code: 'R352',
    level: 'warning',
    description: 'DOB indicates an age between 13 and 15',
    notes:
      'Age is calculated based on the DOB on record. If there is no DOB on record, age is calculated based on input DOB.',
  },
  R353: {
    code: 'R353',
    level: 'warning',
    description: 'DOB indicates an age between 16 and 17',
    notes:
      'Age is calculated based on the DOB on record. If there is no DOB on record, age is calculated based on input DOB.',
  },
  R354: {
    code: 'R354',
    level: 'warning',
    description: 'DOB indicates age is correlated with higher fraud rates',
    notes:
      'DOB indicates the consumer age is between 75 and 120 and may be associated with a higher propensity of fraud. Age is calculated based on the DOB on record. If there is no DOB on record, age is calculated off of the input DOB.',
  },
  R401: {
    code: 'R401',
    level: 'info',
    description: 'Device token was not previously encountered',
    // notes: "This reason code will be triggered when a deviceSessionId is returned in a valid format but is found not to correspond to an actual deviceSessionId. In contrast, if the deviceSessionId is deemed invalid, the R402 - Invalid device token will be triggered."
  },
  R402: {
    code: 'R402',
    level: 'warning',
    description: 'Invalid device token',
  },
  R403: {
    code: 'R403',
    level: 'warning',
    description: 'Device is a virtual device',
  },
  R405: {
    code: 'R405',
    level: 'warning',
    description: 'Device token was not provided',
  },
  R408: {
    code: 'R408',
    level: 'warning',
    description: 'Associated device is likely to be an emulator',
  },
  R409: {
    code: 'R409',
    level: 'warning',
    description:
      'Associated device is connected to mobile network which are historically risky',
  },
  R410: {
    code: 'R410',
    level: 'warning',
    description:
      'Application may not be from primary user associated with the device and looks risky',
  },
  R411: {
    code: 'R411',
    level: 'warning',
    description: 'Device has an uncommon language set for North America',
  },
  R412: {
    code: 'R412',
    level: 'warning',
    description:
      "IP the request was made from does not match the device's IP address",
  },
  R413: {
    code: 'R413',
    level: 'warning',
    description:
      "Device's IP address is more than 3 timezones away from the IP the request is made from",
  },
  R414: {
    code: 'R414',
    level: 'warning',
    description: 'Device operating system is running an uncommon/old version',
  },
  R520: {
    code: 'R520',
    level: 'warning',
    description: 'Email address is considered high risk',
    // notes: "The emailRisk score is equal to or greater than 0.97."
  },
  R551: {
    code: 'R551',
    level: 'warning',
    description: 'Email address is invalid',
    notes:
      'Email address does not meet standards, and the address does not exist at that server.',
  },
  R557: {
    code: 'R557',
    level: 'warning',
    description: 'Email address is disposable',
  },
  R559: {
    code: 'R559',
    level: 'warning',
    description: 'Email address cannot be resolved to the individual',
  },
  R560: {
    code: 'R560',
    level: 'warning',
    description: 'Majority of the characters in the email are numbers',
    notes:
      'More than half of the characters in the email username were found to be numbers, indicating a high risk of the email being a burner or tumbled.',
  },
  R561: {
    code: 'R561',
    level: 'warning',
    description: 'Email address is less than 180 days old',
    notes:
      'Email address was first seen on a verifiable public network source less than 180 days prior to the date of this check. This is generally indicative of a high-risk email as a low email age is a good indicator of malicious activity.',
  },
  R562: {
    code: 'R562',
    level: 'warning',
    description:
      'Domain at which email address was registered is less than 180 days old',
  },
  R563: {
    code: 'R563',
    level: 'warning',
    description: 'Email address domain is invalid',
  },
  R564: {
    code: 'R564',
    level: 'warning',
    description: 'Email address username is invalid',
  },
  R565: {
    code: 'R565',
    level: 'warning',
    description: 'Email address domain represents high risk',
  },
  R566: {
    code: 'R566',
    level: 'warning',
    description: 'Email address has not been seen before',
    notes:
      'Email address could not be found on a public network source. This could also mean that the email recipient is unreachable and hence unverifiable.',
  },
  R567: {
    code: 'R567',
    level: 'warning',
    description: 'Email address top-level domain represents high risk',
  },
  R568: {
    code: 'R568',
    level: 'warning',
    description: 'Email username appears to be scrambled',
    notes:
      'A measure of volatility in the email username that indicates that the username is likely to be a scrambled email, such as "dhfjkska1@example.com".',
  },
  R569: {
    code: 'R569',
    level: 'warning',
    description: 'Email string is unusually long',
    notes:
      'Number of characters in the email string is longer than the usual email string lengths seen.',
  },
  R571: {
    code: 'R571',
    level: 'warning',
    description:
      'Low activity observed on the email address in the last 180 days',
  },
  R572: {
    code: 'R572',
    level: 'warning',
    description:
      'Email associated with more than 2 phone numbers in the last 2 years',
  },
  R573: {
    code: 'R573',
    level: 'warning',
    description:
      'Email handle contains patterns of characters that exhibit an unusually low frequency of occurrences',
    notes:
      'Email intelligence indicates the presence of multiple uncommon character patterns within the input email handle',
  },
  R574: {
    code: 'R574',
    level: 'warning',
    description:
      'Email handle is composed of more than 50% duplicate characters',
  },
  R575: {
    code: 'R575',
    level: 'warning',
    description: 'High probability of tumbling activity in input email',
  },
  R576: {
    code: 'R576',
    level: 'warning',
    description: 'Email with gmail.com domain has an alias',
  },
  R577: {
    code: 'R577',
    level: 'warning',
    description: 'Email handle has an abnormal number pattern',
  },
  R578: {
    code: 'R578',
    level: 'warning',
    description: 'Email handle has an abnormal special character pattern',
  },
  R579: {
    code: 'R579',
    level: 'warning',
    description: 'Email handle has an abnormal pattern',
    notes:
      'Upon analyzing patterns generated from the email handle, it is observed that the email deviates from the typical format of email addresses commonly associated with non-fraudulent users',
  },
  R580: {
    code: 'R580',
    level: 'warning',
    description: 'Email handle exhibits abnormal input behavior',
    notes:
      'Upon analyzing the keyboard patterns, it appears that the pattern exhibited by the email handle deviates from the norm',
  },
  R581: {
    code: 'R581',
    level: 'warning',
    description:
      'Email domain appears to occur far more frequently than usual based on consortium data',
    notes:
      'It has been observed that occurrences of domains significantly surpassing the typical volume can correlate with increased fraud rates',
  },
  R582: {
    code: 'R582',
    level: 'warning',
    description: 'Email was first seen less than 30 days ago',
  },
  R583: {
    code: 'R583',
    level: 'warning',
    description: 'Email was first seen 30-60 days ago',
  },
  R584: {
    code: 'R584',
    level: 'warning',
    description: 'Email was first seen 60-90 days ago',
  },
  R585: {
    code: 'R585',
    level: 'warning',
    description: 'Email was first seen 90-180 days ago',
  },
  R586: {
    code: 'R586',
    level: 'warning',
    description: 'Email originates from a high-risk OFAC sanctioned country',
    notes:
      'Domain name is based on the OFAC list of sanctioned countries and corresponding standard domains for those countries from IANA.',
  },
  R587: {
    code: 'R587',
    level: 'warning',
    description: 'Email is a possible fake email',
  },
  R588: {
    code: 'R588',
    level: 'warning',
    description: 'Email is a possible distribution list',
    notes:
      'Email ID represents typical distribution lists common across organizations and easily abused by fraudsters.',
  },
  R601: {
    code: 'R601',
    level: 'warning',
    description:
      'Phone number associated address is not correlated with the input address',
  },
  R602: {
    code: 'R602',
    level: 'warning',
    description: 'Phone number associated address is not within United States',
  },
  R603: {
    code: 'R603',
    level: 'warning',
    description: 'Phone number is invalid',
  },
  R604: {
    code: 'R604',
    level: 'warning',
    description:
      'Phone number has not been actively used within the past 90 days',
  },
  R605: {
    code: 'R605',
    level: 'warning',
    description:
      'Phone number has not been actively used for more than 90 days',
  },
  R606: {
    code: 'R606',
    level: 'warning',
    description:
      'There has been minimal activity associated with the phone number',
  },
  R607: {
    code: 'R607',
    level: 'info',
    description:
      'Phone number associated address is partially correlated with the input address',
  },
  R608: {
    code: 'R608',
    level: 'warning',
    description: 'Phone number cannot be resolved to the individual',
  },
  R610: {
    code: 'R610',
    level: 'warning',
    description:
      'IP address geolocation places user more than 100 miles from address',
  },
  R611: {
    code: 'R611',
    level: 'warning',
    description: 'Phone number is not allowed for consumers',
  },
  R613: {
    code: 'R613',
    level: 'warning',
    description: 'Phone number is fixed or traditional VoIP',
  },
  R614: {
    code: 'R614',
    level: 'warning',
    description: 'Phone number is toll-free',
  },
  R615: {
    code: 'R615',
    level: 'warning',
    description: 'Phone number is non-fixed or over-the-top VoIP',
  },
  R616: {
    code: 'R616',
    level: 'info',
    description: 'Phone number is prepaid',
  },
  R617: {
    code: 'R617',
    level: 'warning',
    description: 'Phone number is not associated with a major carrier',
  },
  R618: {
    code: 'R618',
    level: 'warning',
    description: 'Phone number has been in service for approx 90-365 days',
  },
  R619: {
    code: 'R619',
    level: 'warning',
    description: 'IP address is a known proxy',
  },
  R620: {
    code: 'R620',
    level: 'warning',
    description: 'Phone number is considered high risk',
    // notes: "The phoneRisk score is equal to or greater than 0.97."
  },
  R622: {
    code: 'R622',
    level: 'warning',
    description: 'Phone number has been in service for less than 30 days',
  },
  R623: {
    code: 'R623',
    level: 'warning',
    description: 'Phone number has been in service for approx 30-90 days',
  },
  R625: {
    code: 'R625',
    level: 'warning',
    description: 'Phone number was ported within the past 24 hours',
  },
  R626: {
    code: 'R626',
    level: 'warning',
    description: 'Phone number was ported within the past 2-7 days',
  },
  R627: {
    code: 'R627',
    level: 'warning',
    description: 'Phone number was ported within the past 7-30 days',
  },
  R628: {
    code: 'R628',
    level: 'warning',
    description: 'Phone number was ported within the past 30-60 days',
  },
  R631: {
    code: 'R631',
    level: 'warning',
    description:
      'Phone subscriber has been correlated with the input phone number for less than 30 days',
  },
  R632: {
    code: 'R632',
    level: 'warning',
    description:
      'Phone subscriber has been correlated with the input phone number for approx 30-90 days',
  },
  R633: {
    code: 'R633',
    level: 'info',
    description:
      'Phone subscriber has been correlated with the input phone number for approx 90-365 days',
  },
  R634: {
    code: 'R634',
    level: 'warning',
    description: 'Phone number was ported within the past 48 hours',
  },
  R639: {
    code: 'R639',
    level: 'warning',
    description:
      'IP address originates from outside of the US or US territories',
  },
  R640: {
    code: 'R640',
    level: 'warning',
    description:
      'IP address originates from a high-risk OFAC sanctioned country',
  },
  R641: {
    code: 'R641',
    level: 'warning',
    description:
      'Proxy is from services that change location to beat DRM, TOR points, temporary proxies and other masking services',
  },
  R642: {
    code: 'R642',
    level: 'warning',
    description:
      'IP address is associated with a cloud hosting provider and is likely a proxy. Users are not usually located in a hosting facility',
  },
  R643: {
    code: 'R643',
    level: 'info',
    description:
      'IP address is associated with a location allowing public internet access',
  },
  R644: {
    code: 'R644',
    level: 'info',
    description: 'IP proxy is cloud-based',
  },
  R645: {
    code: 'R645',
    level: 'warning',
    description: 'IP proxy is TOR-based',
  },
  R646: {
    code: 'R646',
    level: 'warning',
    description: 'IP proxy is VPN-based',
  },
  R647: {
    code: 'R647',
    level: 'info',
    description: 'IP proxy is cloud-security-based',
  },
  R648: {
    code: 'R648',
    level: 'warning',
    description: 'Phone number has had a name change in the last 3 days',
  },
  R650: {
    code: 'R650',
    level: 'warning',
    description: 'IP address is associated with an anonymizing proxy',
  },
  R652: {
    code: 'R652',
    level: 'warning',
    description: 'IP address is associated with spam activity',
  },
  R653: {
    code: 'R653',
    level: 'warning',
    description: 'IP address origin & usage indicates non-human traffic',
  },
  R654: {
    code: 'R654',
    level: 'warning',
    description: 'Phone number seen in frozen credit files',
    notes:
      'A security freeze was initiated on a credit file containing this phone number and has not yet been lifted',
  },
  R655: {
    code: 'R655',
    level: 'warning',
    description: 'Phone number was first seen less than 50 days ago',
  },
  R656: {
    code: 'R656',
    level: 'warning',
    description:
      'Phone number seen in credit files flagged as having initial fraud alerts',
    notes:
      'Credit files containing this phone number have been placed in the initial fraud alert for at least one of the credit bureaus. Initial fraud alerts typically last for a year and need to be removed by request for successful new account openings.',
  },
  R657: {
    code: 'R657',
    level: 'warning',
    description:
      'Last name has not been used with the phone number in the last 2 years',
  },
  R658: {
    code: 'R658',
    level: 'warning',
    description:
      'Last name has not been primarily used with the phone number for more than 2 years',
  },
  R659: {
    code: 'R659',
    level: 'warning',
    description:
      'Phone number has been associated with more than two email addresses in the last 60 days',
  },
  R660: {
    code: 'R660',
    level: 'warning',
    description:
      'Two or more addresses are marked as current in the phone public records',
    notes:
      'There are multiple unique addresses across the different credit header data records associated with the phone.',
  },
  R661: {
    code: 'R661',
    level: 'info',
    description: 'No credit inquiry was found against phone number',
  },
  R662: {
    code: 'R662',
    level: 'info',
    description: 'Phone number was first seen less than 60 days ago',
  },
  R663: {
    code: 'R663',
    level: 'warning',
    description: 'IP Distance From Address >300 miles',
    notes:
      "We have noticed higher fraud rates when a consumer's IP to physical address distance is > 300 miles",
  },
  R664: {
    code: 'R664',
    level: 'warning',
    description: 'Phone number and SSN show low correlation',
  },
  R665: {
    code: 'R665',
    level: 'warning',
    description: 'Presented identity shows low overall correlation',
  },
  R666: {
    code: 'R666',
    level: 'warning',
    description: 'Phone characteristics show anomalous behavior',
  },
  R667: {
    code: 'R667',
    level: 'warning',
    description: 'Presented identity uses uncommon communication channels',
  },
  R668: {
    code: 'R668',
    level: 'warning',
    description: 'Application characteristics match an ongoing anomaly',
  },
  R669: {
    code: 'R669',
    level: 'warning',
    description: 'PII operates > 1,000 miles from usual location',
  },
  R670: {
    code: 'R670',
    level: 'warning',
    description:
      'Phone and email characteristics are unlikely to belong together',
  },
  R671: {
    code: 'R671',
    level: 'info',
    description:
      'There is a more recent phone number associated with the identity',
  },
  R693: {
    code: 'R693',
    level: 'info',
    description:
      'SIM Swap service could not provide information for phone number',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R694: {
    code: 'R694',
    level: 'warning',
    description:
      'SIM Swap service - the input phone number is invalid, inactive, suspended, or in the grace period prior to reassignment',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R695: {
    code: 'R695',
    level: 'info',
    description: 'SIM swap data is not available for the input phone number',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R696: {
    code: 'R696',
    level: 'warning',
    description:
      'SIM Swap service has incurred an error while looking up phone number',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R697: {
    code: 'R697',
    level: 'info',
    description:
      'SIM Swap Service could not look up phone number (unsupported country)',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R698: {
    code: 'R698',
    level: 'info',
    description:
      'SIM Swap Service could not look up phone number (unsupported carrier or a landline)',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R699: {
    code: 'R699',
    level: 'warning',
    description:
      'A SIM Swap was detected within the specified timeframe for the provided phone number',
    // notes: "This reason code is only available when Real-Time SIM Swap Verification service is enabled."
  },
  R701: {
    code: 'R701',
    level: 'warning',
    description: 'Address is inactive, not receiving mail, or vacant',
  },
  R702: {
    code: 'R702',
    level: 'warning',
    description: 'Address is not confirmed as deliverable',
  },
  R703: {
    code: 'R703',
    level: 'warning',
    description: 'Address is invalid or does not exist',
  },
  R704: {
    code: 'R704',
    level: 'warning',
    description: 'Address is a correctional facility',
    notes:
      "Covers the following correctional facilities on a national scale: County jails, State correctional agencies (headquarters, internal department heads, adult institutions, women's institutions, youth institutions, adult probation and parole, juvenile probation and parole), Federal Bureau of Prisons, Federal correctional institutions. Correlated with a higher propensity (16x) for fraud.",
  },
  R705: {
    code: 'R705',
    level: 'warning',
    description: 'Address cannot be resolved to the individual',
  },
  R707: {
    code: 'R707',
    level: 'warning',
    description:
      'Address is a commercial mail receiving agency or commercial mail drop',
    notes:
      'A commercial mail receiving agency (CMRA) is a private business that accepts mail from the Postal Service on behalf of third parties. Also known as a mail drop, CMRAs include general delivery, UPS mail drops, and mailboxes.',
  },
  R708: {
    code: 'R708',
    level: 'warning',
    description: 'Address is a PO Box',
  },
  R709: {
    code: 'R709',
    level: 'warning',
    description: 'Address is commercial or dual-purpose',
  },
  R710: {
    code: 'R710',
    level: 'warning',
    description: 'Address is a military location',
  },
  R711: {
    code: 'R711',
    level: 'warning',
    description:
      'Address is a unique ZIP code, or corporate only, routing all mail internally by the assigned organization rather than by the USPS',
  },
  R712: {
    code: 'R712',
    level: 'warning',
    description:
      'Unable to verify ZIP code for the input address, city and state combination',
  },
  R713: {
    code: 'R713',
    level: 'warning',
    description: 'Address mismatch between city, state, and ZIP code',
  },
  R714: {
    code: 'R714',
    level: 'success',
    description: 'Address was fuzzy matched to the address on record',
    notes:
      'Fuzzy matching an address involves enhancing the address input to match a clean, canonical standard. This technique helps maximize the accuracy of address matching and downstream address processing, ensuring the address resolves to the intended unique location. Some of the changes may include but are not limited to the following: Use complete standard abbreviations instead of non-standard or spelled-out versions; for example, use St instead of Street. Add, remove, or change cardinal directions as needed; for example, change Main St to S Main St if it is a southern street. Correct commonly misspelled street names; for example, change RODEO Dr to RODEO Rd. Rearrange the suite or apartment number order; for example, change #2R 123 Main St to 123 Main St #2R. Remove or standardize commonly used aliases; for example, change CR to County Road if County Road is an alias for CR in that location. These standardization techniques aim to facilitate accurate matching and geocoding of the address to the correct geographic location and point of interest.',
  },
  R720: {
    code: 'R720',
    level: 'warning',
    description: 'Address is considered high risk',
    // notes: "The addressRisk score is equal to or greater than 0.97."
  },
  R721: {
    code: 'R721',
    level: 'warning',
    description: 'Email address is not correlated with the physical address',
    notes:
      'Email address does not have a city/state/ZIP correlation with the input physical address.',
  },
  R722: {
    code: 'R722',
    level: 'warning',
    description: 'State or ZIP was possibly mistyped',
    notes:
      "State or ZIP code was corrected during address standardization to resolve to a valid address. This correction does not modify the applicant's address in the Customer Information File (CIF) and serves only as an indication of a mismatch in the input address that was resolved during the process.",
  },
  R723: {
    code: 'R723',
    level: 'warning',
    description: 'Address is a virtual mailbox',
    notes:
      'Virtual mailboxes allow users to receive packages and postal mail through a virtual home address. Triggered based on an internally maintained database of approximately 3,000 known virtual mailboxes from the top 10 largest virtual mailbox providers in the US.',
  },
  R724: {
    code: 'R724',
    level: 'warning',
    description: 'Address is a filler address',
    notes:
      'Filler addresses are often used in testing scenarios when the actual address is unavailable. However, they can pose a risk, as fraudsters may use them to test fraud strategies and identify scenarios that could lead to approvals.',
  },
  R725: {
    code: 'R725',
    level: 'warning',
    description: 'Two or more recent addresses found for matched identity',
    notes:
      'Multiple addresses were observed in association with the same identity within a short timeframe.',
  },
  R726: {
    code: 'R726',
    level: 'warning',
    description:
      'Address is older than 5 years and a more recent address was found for the matched identity',
  },
  R810: {
    code: 'R810',
    level: 'warning',
    description: 'Document pattern and layout integrity check failed',
    notes:
      'Detected a deviation from the template of the input document against the expected pattern for the same document as defined by the issuing authority standards.',
  },
  R819: {
    code: 'R819',
    level: 'warning',
    description: 'Document fails liveness check',
  },
  R820: {
    code: 'R820',
    level: 'warning',
    description: 'Document headshot has been modified',
  },
  R822: {
    code: 'R822',
    level: 'warning',
    description: 'First name does not match the document',
  },
  R823: {
    code: 'R823',
    level: 'warning',
    description: 'Last name does not match the document',
  },
  R824: {
    code: 'R824',
    level: 'warning',
    description: 'Address does not match the document',
  },
  R825: {
    code: 'R825',
    level: 'warning',
    description: 'DOB does not match the document',
  },
  R826: {
    code: 'R826',
    level: 'warning',
    description: 'Document number does not match uploaded document',
  },
  R827: {
    code: 'R827',
    level: 'warning',
    description: 'Document is expired',
  },
  R831: {
    code: 'R831',
    level: 'warning',
    description: 'Cannot extract the minimum information from barcode',
  },
  R833: {
    code: 'R833',
    level: 'warning',
    description: 'Cannot extract the minimum required information from MRZ',
  },
  R834: {
    code: 'R834',
    level: 'warning',
    description: 'Self-portrait fails liveness check',
  },
  R836: {
    code: 'R836',
    level: 'warning',
    description: 'Document image does not correlate with self-portrait',
  },
  R838: {
    code: 'R838',
    level: 'warning',
    description:
      'Minimum amount of information cannot be extracted from document',
  },
  R845: {
    code: 'R845',
    level: 'warning',
    description: 'Minimum age criteria not met',
  },
  R850: {
    code: 'R850',
    level: 'warning',
    description: 'Self-portrait or the headshot is not usable for Facial Match',
  },
  R853: {
    code: 'R853',
    level: 'warning',
    description: 'Unable to classify the ID or this is an unsupported ID type',
  },
  R856: {
    code: 'R856',
    level: 'warning',
    description: 'Obstructions on the face affecting the liveness check',
  },
  R857: {
    code: 'R857',
    level: 'warning',
    description: 'No face found in the selfie frame',
  },
  R858: {
    code: 'R858',
    level: 'warning',
    description:
      "Age on the document doesn't correlate with the selfie predicted age",
  },
  R859: {
    code: 'R859',
    level: 'warning',
    description: 'ID front correlates with another submitted ID front',
  },
  R861: {
    code: 'R861',
    level: 'warning',
    description:
      'Data extracted from document does not match with barcode or MRZ',
  },
  R862: {
    code: 'R862',
    level: 'warning',
    description: 'Document returned as unknown classification',
  },
  R863: {
    code: 'R863',
    level: 'warning',
    description: 'Incorrect side of document uploaded',
  },
  R895: {
    code: 'R895',
    level: 'success',
    description: 'First Name was fuzzy-matched with the document',
  },
  R896: {
    code: 'R896',
    level: 'success',
    description: 'Last Name was fuzzy-matched with the document',
  },
  R897: {
    code: 'R897',
    level: 'success',
    description: 'DOB was fuzzy-matched with the document',
  },
  R901: {
    code: 'R901',
    level: 'warning',
    description: 'SSN does not match resolved identity',
  },
  R902: {
    code: 'R902',
    level: 'warning',
    description: 'Name does not match resolved identity',
  },
  R907: {
    code: 'R907',
    level: 'warning',
    description: 'SSN has been reported as deceased',
    notes:
      'Another source outside of the SSA may have confirmed the input SSN is deceased.',
  },
  R909: {
    code: 'R909',
    level: 'warning',
    description: 'Resolved identity has been reported as deceased',
  },
  R911: {
    code: 'R911',
    level: 'warning',
    description: 'SSN issued prior to DOB',
    notes:
      'SSN issuance year is determined from SSA data. While the comprehensive KYC validation protocols maintain exceptional accuracy standards, we acknowledge and account for the minimal statistical variations inherent in large-scale as is data aggregation. Up to 0.001% of records can be false positives due to: Reissued SSNs – Before 2011, the SSA occasionally reassigned SSNs that were mistakenly assigned due to clerical errors. Pre-Assigned SSNs – Some SSNs were issued before birth when hospitals or parents applied early. This was uncommon but occasionally happened before the Enumeration at Birth (EAB) program began in 1987. Delayed Birth Registration – Late “at-home” birth filings may cause mismatches.',
  },
  R913: {
    code: 'R913',
    level: 'warning',
    description: 'SSN is invalid',
  },
  R916: {
    code: 'R916',
    level: 'warning',
    description:
      'Address is invalid, does not exist or loosely matches multiple locations',
    notes:
      'Indicates there is no single match against street address and ZIP code, or street address and city/state. These are the same conditions in which USPS determines an address valid and deliverable. May fire in instances where a partial input may resolve to two or more addresses. For example the input "4004 SE 89th Oklahoma OK 73135" is considered invalid because it could resolve to either "4004 SE 89th St Oklahoma City OK 73135" or "4004 SE 89th Ter Oklahoma City OK 73135", both of which are valid.',
  },
  R919: {
    code: 'R919',
    level: 'warning',
    description: 'Address cannot be resolved to the individual',
    notes:
      'Address could not be reliably matched to the individual. This situation may occur under the following circumstances: The address is valid, but there is insufficient data to establish a connection between the individual and the address (Unknown). The address is valid, and there is sufficient data to contradict a connection between the individual and the address (Known mismatch). The address appears fabricated and cannot be verified as real. May be triggered without confirmed mismatches to identify addresses unrelated to the individual.',
  },
  R920: {
    code: 'R920',
    level: 'warning',
    description: 'Identity has an unusual gap in address history',
  },
  R922: {
    code: 'R922',
    level: 'warning',
    description: 'Date of birth cannot be resolved to the individual',
  },
  R923: {
    code: 'R923',
    level: 'warning',
    description: 'SSN seems to be off by 1-2 digits',
    notes:
      'SSN is either: One digit off: A single digit different from the one on record (e.g., 123-45-6789 vs. 123-45-6788 ).Two digits off: The SSN might have two digits different from the ones on record, but without a specific requirement for them to be in the same positions (e.g., 987-65-4321 vs. 980-05-4321).Two digits transposed: Two digits in the SSN might be switched or swapped in position compared to the corresponding digits in the SSN on record (e.g., 456-78-9123 vs. 456-79-8123).',
  },
  R924: {
    code: 'R924',
    level: 'success',
    description: 'Address was fuzzy matched to the address on record',
    notes:
      'Fuzzy matching an address involves enhancing the address input to match a clean, canonical standard. This technique helps maximize the accuracy of address matching and downstream address processing, ensuring the address resolves to the intended unique location. Some of the changes may include but are not limited to the following: Use complete standard abbreviations instead of non-standard or spelled-out versions; for example, use St instead of Street. Add, remove, or change cardinal directions as needed; for example, change Main St to S Main St if it is a southern street. Correct commonly misspelled street names; for example, change RODEO Dr to RODEO Rd. Rearrange the suite or apartment number order; for example, change #2R 123 Main St to 123 Main St #2R. Remove or standardize commonly used aliases; for example, change CR to County Road if County Road is an alias for CR in that location. These standardization techniques aim to facilitate accurate matching and geocoding of the address to the correct geographic location and point of interest.',
  },
  R927: {
    code: 'R927',
    level: 'warning',
    description: 'Full name does not match resolved identity',
  },
  R928: {
    code: 'R928',
    level: 'warning',
    description: 'SSN is associated with multiple last names',
    notes:
      'SSN associated with three or more last names. Causes may include hyphenated names, name changes for marriage or divorce, or typos in another database.',
  },
  R930: {
    code: 'R930',
    level: 'warning',
    description: 'First name does not match resolved identity',
    notes: 'Only last name matched.',
  },
  R932: {
    code: 'R932',
    level: 'warning',
    description: 'Address is a correctional facility',
    notes:
      "Covers the following correctional facilities on a national scale: County jails, State correctional agencies (headquarters, internal department heads, adult institutions, women's institutions, youth institutions, adult probation and parole, juvenile probation and parole), Federal Bureau of Prisons, Federal correctional institutions. Correlated with a higher propensity (16x) for fraud.",
  },
  R933: {
    code: 'R933',
    level: 'warning',
    description: 'Last name not associated with SSN',
  },
  R934: {
    code: 'R934',
    level: 'warning',
    description: 'First name not associated with SSN',
  },
  R939: {
    code: 'R939',
    level: 'warning',
    description: 'SSN is associated with first name but not the last name',
  },
  R940: {
    code: 'R940',
    level: 'warning',
    description: 'SSN not found in public records',
  },
  R941: {
    code: 'R941',
    level: 'warning',
    description: 'SSN is correlated with at least 1 other name and address',
    notes:
      'SSN is associated with three or more identities defined by combinations of first name, last name, and street address (does not include city, state, and ZIP code).',
  },
  R942: {
    code: 'R942',
    level: 'warning',
    description: 'Resolved identity has more than 1 SSN on record',
  },
  R943: {
    code: 'R943',
    level: 'success',
    description: 'First name was fuzzy matched to the name on record',
  },
  R944: {
    code: 'R944',
    level: 'success',
    description: 'Last name was fuzzy matched to the name on record',
    notes:
      "Fuzzy match indicates the last name may have been mistyped, but is still resolved to the best-matched identity. This is common when the name on file is hyphenated but only one of the last names was provided (for example, Ramirez-Gutierrez but only Ramirez was provided) or when there's a suffix appended to the name (Johnson Jr.).",
  },
  R945: {
    code: 'R945',
    level: 'warning',
    description: 'Identity has more than one DOB on record',
  },
  R946: {
    code: 'R946',
    level: 'success',
    description: 'DOB was fuzzy matched to the DOB on record',
    notes:
      'DOB was not an exact match on YYYYMMDD but is within acceptable DOB tolerance.',
  },
  R947: {
    code: 'R947',
    level: 'warning',
    description: 'SSN is not the primary SSN for the resolved identity',
    notes:
      "This can happen due to: Identity theft victims who have changed their SSN. (*) Customers providing an alternate SSN such as an ITIN instead of SSN. Errors or changes in the ID number we have on file. Fraudsters using someone's identity with another SSN so often that the incorrect one gets marked as the primary. (*)(*) These are rare occurrences. This is not an automatic indicator of fraud, as there are legitimate reasons for such discrepancies.",
  },
  R948: {
    code: 'R948',
    level: 'info',
    description: 'Address mismatch between city, state, and ZIP code',
    notes:
      "Either the input city, state, or ZIP code doesn't match the normalized input address, or was missing from the consumer's input credentials. This does not indicate the address is invalid as an address is still considered deliverable when the street matches the city and state or the street matches the ZIP code.",
  },
  R950: {
    code: 'R950',
    level: 'warning',
    description: 'SSN replaced with ITIN',
    notes:
      'Individual has both ITIN and SSN on file, and provided the ITIN. Individual may have both when initially new to the country with an ITIN, and later receives a permanent SSN.',
  },
  R954: {
    code: 'R954',
    level: 'warning',
    description: 'SSN provided was found to be an ITIN',
    notes:
      "ITINs are nine-digit numbers that begin with the number nine and have a range of numbers from '50' to '65', '70' to '88', '90' to '92', and '94' to '99' for the fourth and fifth digits. Individuals have three consecutive years to use their ITIN at least once on a federal tax return - as a filer or as a dependent - before it expires due to non-use.",
  },
  R955: {
    code: 'R955',
    level: 'warning',
    description: 'SSN matches multiple identities',
    notes:
      'SSN is associated with three or more last name and DOB combinations. Spousal and familial relationships make up approximately 20% of this firing rate.',
  },
  R956: {
    code: 'R956',
    level: 'warning',
    description: 'Identity is correlated with multiple SSN/ITINs',
    notes:
      'Three or more SSN/ITINs were found within the identity record and may include mistyped SSN/ITINs. ',
  },
  R957: {
    code: 'R957',
    level: 'warning',
    description: 'DOB is not on record and cannot be verified',
  },
  R961: {
    code: 'R961',
    level: 'warning',
    description: 'Address ZIP code is invalid',
    notes:
      'ZIP code does not correspond with the city and state. This does not indicate the address is invalid as an address is still considered deliverable when the street matches the city and state or the street matches the ZIP code.',
  },
  R963: {
    code: 'R963',
    level: 'warning',
    description: 'Address ZIP code only serves PO Boxes',
    notes:
      'May include addresses that fall outside of the traditional service areas for USPS, such as rural locations served only by P.O. Boxes.',
  },
  R964: {
    code: 'R964',
    level: 'info',
    description: 'Address is a commercial or institutional address',
    notes:
      'Commercial addresses can include apartment complexes/buildings with unit numbers that cannot be resolved. Institutional addresses can include administrative buildings. Military addresses and dormitories are designated separately.',
  },
  R965: {
    code: 'R965',
    level: 'warning',
    description: 'Address is an embassy or consulate',
    notes:
      "Address belongs to a foreign country's official embassy or consulate.",
  },
  R972: {
    code: 'R972',
    level: 'warning',
    description:
      'Address is a commercial mail receiving agency or commercial mail drop',
    notes:
      'A commercial mail receiving agency (CMRA) is a private business that accepts mail from the Postal Service on behalf of third parties. Also known as a mail drop, CMRAs include general delivery, UPS mail drops, and mailboxes.',
  },
  R973: {
    code: 'R973',
    level: 'warning',
    description:
      'Address is a unique ZIP code, or corporate only, routing all mail internally by the assigned organization rather than by the USPS',
    notes:
      'Unique ZIP codes are high volume mail receivers that receive mail at one location and then distribute the mail internally. Unique ZIP codes generally are used by large organizations, government buildings, universities, or large medical facilities.',
  },
  R976: {
    code: 'R976',
    level: 'warning',
    description: 'Identity is associated with an active duty alert',
    notes:
      'Active duty alerts last for one year and can be renewed to match the period of deployment. Correlated with a higher propensity (8x) for fraud.',
  },
  R977: {
    code: 'R977',
    level: 'warning',
    description:
      'Identity is associated with an active duty alert and a fraud victim initial alert',
    notes:
      'Active duty alerts last for one year and can be renewed to match the period of deployment. Initial fraud victim alerts last for one year and are generated by the consumer via a request to one or more credit reporting agencies. Correlated with a higher propensity (6x) for fraud.',
  },
  R978: {
    code: 'R978',
    level: 'warning',
    description: 'Identity is associated with a fraud victim initial alert',
    notes:
      'An initial fraud victim alert lasts for one year and is generated by the consumer via a request to one or more credit reporting agencies. Correlated with a higher propensity (17x) for fraud.',
  },
  R979: {
    code: 'R979',
    level: 'warning',
    description:
      'Identity is associated with an active duty alert and a fraud victim extended alert',
    notes:
      'Active duty alerts last for one year and can be renewed to match the period of deployment. Fraud victim extended alerts last for seven years and are only available to identity theft victims who have filed a police report or a Federal Trade Commission Identity Theft Report. Correlated with a higher propensity (16x) for fraud.',
  },
  R980: {
    code: 'R980',
    level: 'warning',
    description: 'Identity is associated with a fraud victim extended alert',
    notes:
      'Fraud victim extended alerts last for seven years and are only available to identity theft victims who have filed a police report or a Federal Trade Commission Identity Theft Report. Correlated with a higher propensity (16x) for fraud.',
  },
  R988: {
    code: 'R988',
    level: 'warning',
    description:
      'Identity has more than one DOB on record and at least one is under the age of 21',
  },
  R995: {
    code: 'R995',
    level: 'info',
    description: 'Better matching SSN found',
    notes:
      "SSN provided is either randomized or isn't found within public records. A better, non-randomized SSN has been found on file.",
  },
  R996: {
    code: 'R996',
    level: 'warning',
    description: 'Permitted entity certification is invalid',
    notes: 'Unexpected issue or fault has occurred within the eCBSV service.',
  },
  R997: {
    code: 'R997',
    level: 'warning',
    description: 'eCBSV service has incurred an error',
    notes: 'Unexpected issue or fault has occurred within the eCBSV service.',
  },
  R998: {
    code: 'R998',
    level: 'warning',
    description: 'Name, SSN and DOB correlation unverified by SSA',
    notes:
      'Identity could not be matched to SSA records under all of the following conditions: First initial matches the first name on record. For example, Elizabeth will match Elijah but nicknames like Betsy are disallowed. Last name is within one edit distance of the last name on record. For example, Denton will match Fenton. SSN matched exactly. Note that ITINs cannot be validated by the SSA. Additionally, the DOB can be: An exact match on YYYY. For example, 1971/03/22 will match any date from 1971/01/01 through 1971/12/31. Within a one year radius if there is an exact match on MM. For example, 1971/03/22 will match any date from 1970/03/23 through 1970/03/31, and 1972/03/01 through 1972/03/21.',
  },
  R999: {
    code: 'R999',
    level: 'warning',
    description: 'Identity decease verified by SSA',
    notes: 'SSA has confirmed that identity is deceased at the federal level.',
  },
  R1001: {
    code: 'R1001',
    level: 'warning',
    description:
      'Found at least 2 distinct DOBs associated with the given identity',
  },
  R1002: {
    code: 'R1002',
    level: 'warning',
    description:
      'Found at least 4 distinct first names associated with the given identity',
  },
  R1003: {
    code: 'R1003',
    level: 'warning',
    description:
      'Found at least 4 distinct last names associated with the given identity',
  },
  R1004: {
    code: 'R1004',
    level: 'warning',
    description:
      'Found at least 3 distinct phone numbers associated with the given identity',
  },
  R1005: {
    code: 'R1005',
    level: 'warning',
    description:
      'Found at least 3 distinct emails associated with the given identity',
  },
  R1006: {
    code: 'R1006',
    level: 'warning',
    description:
      'Found at least 1 application marked as fraudulent associated with the given identity',
  },
  R1007: {
    code: 'R1007',
    level: 'warning',
    description:
      'Found at least 1 application marked as third party fraud associated with the given identity',
  },
  R1008: {
    code: 'R1008',
    level: 'info',
    description:
      'Found at least 2 applications within 30 days of each other associated with the given identity',
  },
  R1009: {
    code: 'R1009',
    level: 'info',
    description:
      'Found at least 2 applications within 90 days of each other associated with the given identity',
  },
  R1010: {
    code: 'R1010',
    level: 'warning',
    description:
      'Found at least 1 account marked as fraudulent associated with the given identity',
  },
  R1011: {
    code: 'R1011',
    level: 'warning',
    description:
      'Found at least 1 account marked as suspected Third-Party Fraud associated with the given identity',
  },
  R1012: {
    code: 'R1012',
    level: 'warning',
    description:
      'Found at least 1 account marked as suspected First-Party Fraud associated with the given identity',
  },
  R1013: {
    code: 'R1013',
    level: 'warning',
    description:
      'Found at least 1 account marked as Other Fraud associated with the given identity',
  },
  R1014: {
    code: 'R1014',
    level: 'warning',
    description:
      'Found at least 1 account marked as Delinquency associated with the given identity',
  },
  R1015: {
    code: 'R1015',
    level: 'warning',
    description:
      'Found at least 1 closed account associated with the given identity',
  },
  R1016: {
    code: 'R1016',
    level: 'warning',
    description:
      'Found at least 5 closed accounts associated with the given identity',
  },
  R1017: {
    code: 'R1017',
    level: 'info',
    description:
      'Found at least 2 accounts that were opened within 30 days from each other associated with the given identity',
  },
  R1018: {
    code: 'R1018',
    level: 'info',
    description:
      'Found at least 2 accounts that were opened within 90 days from each other associated with the given identity',
  },
  R1019: {
    code: 'R1019',
    level: 'warning',
    description:
      'Found at least 2 accounts that were closed within 30 days from each other associated with the given identity',
  },
  R1020: {
    code: 'R1020',
    level: 'warning',
    description:
      'Found at least 2 accounts that were reported as Fraud within 30 days from each other associated with the given identity',
  },
  R1021: {
    code: 'R1021',
    level: 'info',
    description:
      'Found at least 1 account that has been closed within 30 days from opening associated with the given identity',
  },
  R1022: {
    code: 'R1022',
    level: 'warning',
    description:
      'Found at least 1 account that has been reported as Fraud within 30 days from opening associated with the given identity',
  },
  R1023: {
    code: 'R1023',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Fraud associated with the given identity',
  },
  R1024: {
    code: 'R1024',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Suspected Third-Party Fraud associated with the given identity',
  },
  R1025: {
    code: 'R1025',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Suspected First-Party Fraud associated with the given identity',
  },
  R1026: {
    code: 'R1026',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Other Fraud associated with the given identity',
  },
  R1027: {
    code: 'R1027',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Delinquency associated with the given identity',
  },
  R1028: {
    code: 'R1028',
    level: 'warning',
    description:
      'Found at least 10 returned ACH transactions associated with the given identity',
  },
  R1029: {
    code: 'R1029',
    level: 'warning',
    description:
      'Found at least 1 returned ACH transaction with return category Fraudulent Behavior associated with the given identity',
  },
  R1030: {
    code: 'R1030',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Fraud within 7 days from one another associated with the given identity',
  },
  R1031: {
    code: 'R1031',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Fraud which was committed within 7 days from account opening associated with the given identity',
  },
  R1032: {
    code: 'R1032',
    level: 'warning',
    description:
      'Found at least 1 ACH transaction reported as Fraud which was committed within 30 days from account opening associated with the given identity',
  },
  R1033: {
    code: 'R1033',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Fraud associated with the given identity',
  },
  R1034: {
    code: 'R1034',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Suspected Third-Party Fraud associated with the given identity',
  },
  R1035: {
    code: 'R1035',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Suspected First-Party Fraud associated with the given identity',
  },
  R1036: {
    code: 'R1036',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Other Fraud associated with the given identity',
  },
  R1037: {
    code: 'R1037',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Delinquency associated with the given identity',
  },
  R1038: {
    code: 'R1038',
    level: 'warning',
    description:
      'Found at least 1 returned Check transaction with a return category Fraudulent Behavior associated with the given identity',
  },
  R1039: {
    code: 'R1039',
    level: 'warning',
    description:
      'Found at least 2 check transactions reported as Fraud within 7 days from one another associated with the given identity',
  },
  R1040: {
    code: 'R1040',
    level: 'warning',
    description:
      'Found at least 2 check transactions reported as Fraud within 30 days from one another associated with the given identity',
  },
  R1041: {
    code: 'R1041',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Fraud which was committed within 7 days from account opening associated with the given identity',
  },
  R1042: {
    code: 'R1042',
    level: 'warning',
    description:
      'Found at least 1 Check transaction reported as Fraud which was committed within 30 days from account opening associated with the given identity',
  },
  R1043: {
    code: 'R1043',
    level: 'warning',
    description:
      'Found at least 10 disputed Card transactions associated with the given identity',
  },
  R1044: {
    code: 'R1044',
    level: 'warning',
    description:
      'Found at least 20 disputed Card transactions associated with the given identity',
  },
  R1045: {
    code: 'R1045',
    level: 'warning',
    description:
      'Found at least 5 Unauthorized disputed Card transactions associated with the given identity',
  },
  R1046: {
    code: 'R1046',
    level: 'warning',
    description:
      'Found at least 5 Authorized disputed Card transactions associated with the given identity',
  },
  R1047: {
    code: 'R1047',
    level: 'warning',
    description:
      'Found at least 5 Granted disputed Card transactions associated with the given identity',
  },
  R1048: {
    code: 'R1048',
    level: 'warning',
    description:
      'Found at least 10 Granted disputed Card transactions associated with the given identity',
  },
  R1049: {
    code: 'R1049',
    level: 'warning',
    description:
      'Found at least 2 disputed Card transactions within 1 day from one another associated with the given identity',
  },
  R1050: {
    code: 'R1050',
    level: 'warning',
    description:
      'Found at least 2 disputed Card transactions within 7 days from one another associated with the given identity',
  },
  R1051: {
    code: 'R1051',
    level: 'warning',
    description:
      'Found at least 1 disputed Card transaction committed within 1 day from account opening associated with the given identity',
  },
  R1052: {
    code: 'R1052',
    level: 'warning',
    description:
      'Found at least 1 disputed Card transaction committed within 7 days from account opening associated with the given identity',
  },
  R1053: {
    code: 'R1053',
    level: 'warning',
    description:
      'Found at least 7 distinct emails associated with the given fullname and date of birth combination',
  },
  R1054: {
    code: 'R1054',
    level: 'warning',
    description:
      'Found at least 7 distinct phones associated with the given identity',
  },
  R1055: {
    code: 'R1055',
    level: 'warning',
    description:
      'Matched identity onto at least 17 other transaction consortium institutions',
  },
  R1056: {
    code: 'R1056',
    level: 'warning',
    description:
      'Matched Fullname and date of birth combination onto at least 16 other transaction consortium institutions',
  },
  R1057: {
    code: 'R1057',
    level: 'warning',
    description:
      'Matched Fullname, date of birth and SSN combination onto at least 10 other transaction consortium institutions',
  },
  R1058: {
    code: 'R1058',
    level: 'warning',
    description:
      'Found at least 4 distinct emails associated with the given identity',
  },
  R1059: {
    code: 'R1059',
    level: 'warning',
    description:
      'Found at least 3-6 distinct emails associated with the given fullname and date of birth combination',
  },
  R1060: {
    code: 'R1060',
    level: 'warning',
    description:
      'Seen input phone number in at least 13 other transaction consortium institutions',
  },
  R1061: {
    code: 'R1061',
    level: 'warning',
    description:
      'Seen input phone number with in at least 4 distinct emails in transaction consortium',
  },
  R1062: {
    code: 'R1062',
    level: 'warning',
    description: 'Found at least 1 fraud event reported for the input identity',
  },
} satisfies Record<string, SocureFinding>;

const accountIntelligence = {
  I301: codes.I301,
  I302: codes.I302,
  I304: codes.I304,
  I305: codes.I305,
  I307: codes.I307,
  I308: codes.I308,
  I310: codes.I310,
  I311: codes.I311,
  I313: codes.I313,
  I314: codes.I314,
  I316: codes.I316,
  I317: codes.I317,
  I319: codes.I319,
  I320: codes.I320,
  I325: codes.I325,
  I326: codes.I326,
  I328: codes.I328,
  I329: codes.I329,
  I330: codes.I330,
  I331: codes.I331,
  I332: codes.I332,
  I333: codes.I333,
  I334: codes.I334,
  I335: codes.I335,
  I336: codes.I336,
  I337: codes.I337,
  I338: codes.I338,
  I339: codes.I339,
  I341: codes.I341,
  I342: codes.I342,
  I343: codes.I343,
  I344: codes.I344,
  R301: codes.R301,
  R302: codes.R302,
  R303: codes.R303,
  R304: codes.R304,
  R305: codes.R305,
  R306: codes.R306,
  R307: codes.R307,
  R309: codes.R309,
  R311: codes.R311,
  R312: codes.R312,
  R313: codes.R313,
  R314: codes.R314,
  R315: codes.R315,
} satisfies Record<string, SocureFinding>;

const addressRisk = {
  I610: codes.I610,
  I704: codes.I704,
  I705: codes.I705,
  I706: codes.I706,
  I707: codes.I707,
  I708: codes.I708,
  I711: codes.I711,
  I712: codes.I712,
  I713: codes.I713,
  I714: codes.I714,
  I715: codes.I715,
  I716: codes.I716,
  I720: codes.I720,
  I721: codes.I721,
  I722: codes.I722,
  R601: codes.R601,
  R602: codes.R602,
  R607: codes.R607,
  R701: codes.R701,
  R702: codes.R702,
  R703: codes.R703,
  R704: codes.R704,
  R705: codes.R705,
  R707: codes.R707,
  R708: codes.R708,
  R709: codes.R709,
  R710: codes.R710,
  R711: codes.R711,
  R712: codes.R712,
  R713: codes.R713,
  R714: codes.R714,
  R720: codes.R720,
  R721: codes.R721,
  R722: codes.R722,
  R723: codes.R723,
  R724: codes.R724,
  R725: codes.R725,
  R726: codes.R726,
} satisfies Record<string, SocureFinding>;

const nameAddressCorrelation = {
  I708: codes.I708,
  I709: codes.I709,
  I710: codes.I710,
  I718: codes.I718,
  I719: codes.I719,
  R701: codes.R701,
  R703: codes.R703,
  R704: codes.R704,
  R705: codes.R705,
} satisfies Record<string, SocureFinding>;

const alertList = {
  R110: codes.R110,
  R111: codes.R111,
  R113: codes.R113,
} satisfies Record<string, SocureFinding>;

const ecbsv = {
  I998: codes.I998,
  I999: codes.I999,
  R996: codes.R996,
  R997: codes.R997,
  R998: codes.R998,
  R999: codes.R999,
} satisfies Record<string, SocureFinding>;

const emailRisk = {
  I520: codes.I520,
  I550: codes.I550,
  I551: codes.I551,
  I553: codes.I553,
  I555: codes.I555,
  I556: codes.I556,
  I560: codes.I560,
  I561: codes.I561,
  I562: codes.I562,
  I563: codes.I563,
  I564: codes.I564,
  I565: codes.I565,
  I566: codes.I566,
  I567: codes.I567,
  I568: codes.I568,
  I569: codes.I569,
  I570: codes.I570,
  I571: codes.I571,
  I572: codes.I572,
  I573: codes.I573,
  I574: codes.I574,
  I575: codes.I575,
  I576: codes.I576,
  I577: codes.I577,
  I578: codes.I578,
  R586: codes.R586,
  R587: codes.R587,
  R588: codes.R588,
  I637: codes.I637,
  I721: codes.I721,
  I722: codes.I722,
  R520: codes.R520,
  R551: codes.R551,
  R557: codes.R557,
  R559: codes.R559,
  R560: codes.R560,
  R561: codes.R561,
  R562: codes.R562,
  R563: codes.R563,
  R564: codes.R564,
  R565: codes.R565,
  R566: codes.R566,
  R567: codes.R567,
  R568: codes.R568,
  R569: codes.R569,
  R571: codes.R571,
  R572: codes.R572,
  R573: codes.R573,
  R574: codes.R574,
  R575: codes.R575,
  R576: codes.R576,
  R577: codes.R577,
  R578: codes.R578,
  R579: codes.R579,
  R580: codes.R580,
  R581: codes.R581,
  R582: codes.R582,
  R583: codes.R583,
  R584: codes.R584,
  R585: codes.R585,
  R721: codes.R721,
} satisfies Record<string, SocureFinding>;

const nameEmailCorrelation = {
  I554: codes.I554,
  I556: codes.I556,
  I557: codes.I557,
  I558: codes.I558,
  I559: codes.I559,
  I572: codes.I572,
  I573: codes.I573,
  R551: codes.R551,
  R557: codes.R557,
  R559: codes.R559,
  R563: codes.R563,
  R564: codes.R564,
} satisfies Record<string, SocureFinding>;

const globalWatchlist = {
  I196: codes.I196,
  R171: codes.R171,
  R172: codes.R172,
  R173: codes.R173,
  R180: codes.R180,
  R181: codes.R181,
  R182: codes.R182,
  R183: codes.R183,
  R184: codes.R184,
  R185: codes.R185,
  R186: codes.R186,
  R187: codes.R187,
  R188: codes.R188,
  R189: codes.R189,
  R190: codes.R190,
  R191: codes.R191,
  R195: codes.R195,
  R640: codes.R640,
} satisfies Record<string, SocureFinding>;

const watchlistStandard = globalWatchlist;
const watchlistPlus = globalWatchlist;
const watchlistPremier = globalWatchlist;

const kyc = {
  I350: codes.I350,
  I351: codes.I351,
  I352: codes.I352,
  I903: codes.I903,
  I904: codes.I904,
  I905: codes.I905,
  I906: codes.I906,
  I907: codes.I907,
  I908: codes.I908,
  I909: codes.I909,
  I910: codes.I910,
  I911: codes.I911,
  I912: codes.I912,
  I917: codes.I917,
  I919: codes.I919,
  I920: codes.I920,
  I921: codes.I921,
  I922: codes.I922,
  I923: codes.I923,
  I930: codes.I930,
  I931: codes.I931,
  I932: codes.I932,
  I933: codes.I933,
  I934: codes.I934,
  I990: codes.I990,
  I998: codes.I998,
  I999: codes.I999,
  R350: codes.R350,
  R351: codes.R351,
  R352: codes.R352,
  R353: codes.R353,
  R354: codes.R354,
  R901: codes.R901,
  R902: codes.R902,
  R907: codes.R907,
  R909: codes.R909,
  R911: codes.R911,
  R913: codes.R913,
  R916: codes.R916,
  R919: codes.R919,
  R920: codes.R920,
  R922: codes.R922,
  R923: codes.R923,
  R924: codes.R924,
  R927: codes.R927,
  R930: codes.R930,
  R932: codes.R932,
  R933: codes.R933,
  R934: codes.R934,
  R939: codes.R939,
  R940: codes.R940,
  R943: codes.R943,
  R944: codes.R944,
  R947: codes.R947,
  R948: codes.R948,
  R950: codes.R950,
  R954: codes.R954,
  R957: codes.R957,
  R961: codes.R961,
  R963: codes.R963,
  R964: codes.R964,
  R965: codes.R965,
  R972: codes.R972,
  R973: codes.R973,
  R976: codes.R976,
  R977: codes.R977,
  R978: codes.R978,
  R979: codes.R979,
  R980: codes.R980,
  R988: codes.R988,
  R995: codes.R995,
  R998: codes.R998,
  R999: codes.R999,
} satisfies Record<string, SocureFinding>;
const kycPlus = kyc;

const optionalCodes = {
  I900: codes.I900,
  I901: codes.I901,
  I902: codes.I902,
  R928: codes.R928,
  R941: codes.R941,
  R942: codes.R942,
  R945: codes.R945,
  R946: codes.R946,
  R955: codes.R955,
  R956: codes.R956,
} satisfies Record<string, SocureFinding>;

const deceasedCheck = {
  I913: codes.I913,
  I914: codes.I914,
  R907: codes.R907,
  R909: codes.R909,
} satisfies Record<string, SocureFinding>;

const phoneRisk = {
  I601: codes.I601,
  I602: codes.I602,
  I605: codes.I605,
  I608: codes.I608,
  I609: codes.I609,
  I610: codes.I610,
  I611: codes.I611,
  I614: codes.I614,
  I616: codes.I616,
  I618: codes.I618,
  I620: codes.I620,
  I621: codes.I621,
  I622: codes.I622,
  I625: codes.I625,
  I626: codes.I626,
  I627: codes.I627,
  I630: codes.I630,
  I631: codes.I631,
  I632: codes.I632,
  I633: codes.I633,
  I634: codes.I634,
  I635: codes.I635,
  I636: codes.I636,
  I637: codes.I637,
  I638: codes.I638,
  I639: codes.I639,
  I640: codes.I640,
  I699: codes.I699,
  R601: codes.R601,
  R602: codes.R602,
  R603: codes.R603,
  R604: codes.R604,
  R605: codes.R605,
  R606: codes.R606,
  R607: codes.R607,
  R608: codes.R608,
  R611: codes.R611,
  R613: codes.R613,
  R614: codes.R614,
  R615: codes.R615,
  R616: codes.R616,
  R617: codes.R617,
  R618: codes.R618,
  R620: codes.R620,
  R622: codes.R622,
  R623: codes.R623,
  R625: codes.R625,
  R626: codes.R626,
  R627: codes.R627,
  R628: codes.R628,
  R631: codes.R631,
  R632: codes.R632,
  R633: codes.R633,
  R634: codes.R634,
  R639: codes.R639,
  R640: codes.R640,
  R641: codes.R641,
  R642: codes.R642,
  R643: codes.R643,
  R644: codes.R644,
  R645: codes.R645,
  R646: codes.R646,
  R647: codes.R647,
  R648: codes.R648,
  R650: codes.R650,
  R652: codes.R652,
  R653: codes.R653,
  R654: codes.R654,
  R655: codes.R655,
  R656: codes.R656,
  R657: codes.R657,
  R658: codes.R658,
  R659: codes.R659,
  R660: codes.R660,
  R661: codes.R661,
  R662: codes.R662,
  R699: codes.R699,
  R671: codes.R671,
  R693: codes.R693,
  R694: codes.R694,
  R695: codes.R695,
  R696: codes.R696,
  R697: codes.R697,
  R698: codes.R698,
} satisfies Record<string, SocureFinding>;

const namePhoneCorrelation = {
  I618: codes.I618,
  I621: codes.I621,
  I622: codes.I622,
  I623: codes.I623,
  I624: codes.I624,
  R603: codes.R603,
  R604: codes.R604,
  R605: codes.R605,
  R608: codes.R608,
} satisfies Record<string, SocureFinding>;

const documentVerification = {
  I808: codes.I808,
  I820: codes.I820,
  I822: codes.I822,
  I823: codes.I823,
  I824: codes.I824,
  I825: codes.I825,
  I826: codes.I826,
  I831: codes.I831,
  I833: codes.I833,
  I834: codes.I834,
  I836: codes.I836,
  I837: codes.I837,
  I838: codes.I838,
  I845: codes.I845,
  I846: codes.I846,
  I847: codes.I847,
  I848: codes.I848,
  I849: codes.I849,
  I854: codes.I854,
  I855: codes.I855,
  I856: codes.I856,
  I859: codes.I859,
  I860: codes.I860,
  R810: codes.R810,
  R819: codes.R819,
  R820: codes.R820,
  R822: codes.R822,
  R823: codes.R823,
  R824: codes.R824,
  R825: codes.R825,
  R826: codes.R826,
  R827: codes.R827,
  R831: codes.R831,
  R833: codes.R833,
  R834: codes.R834,
  R836: codes.R836,
  R838: codes.R838,
  R845: codes.R845,
  R850: codes.R850,
  R853: codes.R853,
  R856: codes.R856,
  R857: codes.R857,
  R858: codes.R858,
  R859: codes.R859,
  R861: codes.R861,
  R862: codes.R862,
  R863: codes.R863,
  R895: codes.R895,
  R896: codes.R896,
  R897: codes.R897,
} satisfies Record<string, SocureFinding>;

const deviceRisk = {
  I401: codes.I401,
  I402: codes.I402,
  I403: codes.I403,
  I404: codes.I404,
  I405: codes.I405,
  I406: codes.I406,
  I407: codes.I407,
  I408: codes.I408,
  I409: codes.I409,
  I410: codes.I410,
  I411: codes.I411,
  I412: codes.I412,
  I413: codes.I413,
  I414: codes.I414,
  I415: codes.I415,
  I416: codes.I416,
  I417: codes.I417,
  I419: codes.I419,
  I420: codes.I420,
  I421: codes.I421,
  I423: codes.I423,
  I424: codes.I424,
  I425: codes.I425,
  I428: codes.I428,
  I429: codes.I429,
  I430: codes.I430,
  I431: codes.I431,
  I631: codes.I631,
  I632: codes.I632,
  I633: codes.I633,
  I634: codes.I634,
  I635: codes.I635,
  I636: codes.I636,
  I638: codes.I638,
  I639: codes.I639,
  R401: codes.R401,
  R402: codes.R402,
  R403: codes.R403,
  R405: codes.R405,
  R408: codes.R408,
  R409: codes.R409,
  R410: codes.R410,
  R411: codes.R411,
  R412: codes.R412,
  R413: codes.R413,
  R639: codes.R639,
  R640: codes.R640,
  R641: codes.R641,
  R642: codes.R642,
  R643: codes.R643,
  R644: codes.R644,
  R645: codes.R645,
  R646: codes.R646,
  R647: codes.R647,
  R650: codes.R650,
  R652: codes.R652,
  R653: codes.R653,
} satisfies Record<string, SocureFinding>;

const deviceIdentityCorrelation = deviceRisk;

const firstPartyFraud = {
  I1001: codes.I1001,
  I1002: codes.I1002,
  I1003: codes.I1003,
  I1004: codes.I1004,
  I1005: codes.I1005,
  R1001: codes.R1001,
  R1002: codes.R1002,
  R1003: codes.R1003,
  R1004: codes.R1004,
  R1005: codes.R1005,
  R1006: codes.R1006,
  R1007: codes.R1007,
  R1008: codes.R1008,
  R1009: codes.R1009,
  R1010: codes.R1010,
  R1011: codes.R1011,
  R1012: codes.R1012,
  R1013: codes.R1013,
  R1014: codes.R1014,
  R1015: codes.R1015,
  R1016: codes.R1016,
  R1017: codes.R1017,
  R1018: codes.R1018,
  R1019: codes.R1019,
  R1020: codes.R1020,
  R1021: codes.R1021,
  R1022: codes.R1022,
  R1023: codes.R1023,
  R1024: codes.R1024,
  R1025: codes.R1025,
  R1026: codes.R1026,
  R1027: codes.R1027,
  R1028: codes.R1028,
  R1029: codes.R1029,
  R1030: codes.R1030,
  R1031: codes.R1031,
  R1032: codes.R1032,
  R1033: codes.R1033,
  R1034: codes.R1034,
  R1035: codes.R1035,
  R1036: codes.R1036,
  R1037: codes.R1037,
  R1038: codes.R1038,
  R1039: codes.R1039,
  R1040: codes.R1040,
  R1041: codes.R1041,
  R1042: codes.R1042,
  R1043: codes.R1043,
  R1044: codes.R1044,
  R1045: codes.R1045,
  R1046: codes.R1046,
  R1047: codes.R1047,
  R1048: codes.R1048,
  R1049: codes.R1049,
  R1050: codes.R1050,
  R1051: codes.R1051,
  R1052: codes.R1052,
  R1053: codes.R1053,
  R1054: codes.R1054,
  R1055: codes.R1055,
  R1056: codes.R1056,
  R1057: codes.R1057,
  R1058: codes.R1058,
  R1059: codes.R1059,
  R1060: codes.R1060,
  R1061: codes.R1061,
  R1062: codes.R1062,
} satisfies Record<string, SocureFinding>;

const fraud = {
  I121: codes.I121,
  I127: codes.I127,
  I212: codes.I212,
  I202: codes.I202,
  I350: codes.I350,
  I351: codes.I351,
  I352: codes.I352,
  I550: codes.I550,
  I551: codes.I551,
  I553: codes.I553,
  I555: codes.I555,
  I556: codes.I556,
  I560: codes.I560,
  I561: codes.I561,
  I562: codes.I562,
  I563: codes.I563,
  I564: codes.I564,
  I565: codes.I565,
  I566: codes.I566,
  I567: codes.I567,
  I568: codes.I568,
  I569: codes.I569,
  I570: codes.I570,
  I571: codes.I571,
  I572: codes.I572,
  I573: codes.I573,
  I574: codes.I574,
  I575: codes.I575,
  I576: codes.I576,
  I577: codes.I577,
  I578: codes.I578,
  I601: codes.I601,
  I602: codes.I602,
  I605: codes.I605,
  I608: codes.I608,
  I609: codes.I609,
  I610: codes.I610,
  I611: codes.I611,
  I614: codes.I614,
  I616: codes.I616,
  I618: codes.I618,
  I621: codes.I621,
  I622: codes.I622,
  I625: codes.I625,
  I626: codes.I626,
  I627: codes.I627,
  I630: codes.I630,
  I631: codes.I631,
  I632: codes.I632,
  I633: codes.I633,
  I634: codes.I634,
  I635: codes.I635,
  I636: codes.I636,
  I637: codes.I637,
  I638: codes.I638,
  I639: codes.I639,
  I640: codes.I640,
  I641: codes.I641,
  I704: codes.I704,
  I705: codes.I705,
  I706: codes.I706,
  I707: codes.I707,
  I708: codes.I708,
  I711: codes.I711,
  I712: codes.I712,
  I713: codes.I713,
  I714: codes.I714,
  I715: codes.I715,
  I716: codes.I716,
  I721: codes.I721,
  I722: codes.I722,
  R201: codes.R201,
  R202: codes.R202,
  R203: codes.R203,
  R204: codes.R204,
  R205: codes.R205,
  R206: codes.R206,
  R207: codes.R207,
  R209: codes.R209,
  R210: codes.R210,
  R211: codes.R211,
  R212: codes.R212,
  R213: codes.R213,
  R214: codes.R214,
  R215: codes.R215,
  R216: codes.R216,
  R217: codes.R217,
  R218: codes.R218,
  R219: codes.R219,
  R350: codes.R350,
  R351: codes.R351,
  R352: codes.R352,
  R353: codes.R353,
  R354: codes.R354,
  R414: codes.R414,
  R551: codes.R551,
  R557: codes.R557,
  R559: codes.R559,
  R560: codes.R560,
  R561: codes.R561,
  R562: codes.R562,
  R563: codes.R563,
  R564: codes.R564,
  R565: codes.R565,
  R566: codes.R566,
  R567: codes.R567,
  R568: codes.R568,
  R569: codes.R569,
  R571: codes.R571,
  R572: codes.R572,
  R573: codes.R573,
  R574: codes.R574,
  R575: codes.R575,
  R576: codes.R576,
  R577: codes.R577,
  R578: codes.R578,
  R579: codes.R579,
  R580: codes.R580,
  R581: codes.R581,
  R582: codes.R582,
  R583: codes.R583,
  R584: codes.R584,
  R585: codes.R585,
  R586: codes.R586,
  R587: codes.R587,
  R588: codes.R588,
  R601: codes.R601,
  R602: codes.R602,
  R603: codes.R603,
  R604: codes.R604,
  R605: codes.R605,
  R606: codes.R606,
  R607: codes.R607,
  R608: codes.R608,
  R610: codes.R610,
  R611: codes.R611,
  R613: codes.R613,
  R614: codes.R614,
  R615: codes.R615,
  R616: codes.R616,
  R617: codes.R617,
  R618: codes.R618,
  R619: codes.R619,
  R622: codes.R622,
  R623: codes.R623,
  R625: codes.R625,
  R626: codes.R626,
  R627: codes.R627,
  R628: codes.R628,
  R631: codes.R631,
  R632: codes.R632,
  R633: codes.R633,
  R634: codes.R634,
  R639: codes.R639,
  R640: codes.R640,
  R641: codes.R641,
  R642: codes.R642,
  R643: codes.R643,
  R644: codes.R644,
  R645: codes.R645,
  R646: codes.R646,
  R647: codes.R647,
  R648: codes.R648,
  R650: codes.R650,
  R652: codes.R652,
  R653: codes.R653,
  R654: codes.R654,
  R655: codes.R655,
  R656: codes.R656,
  R657: codes.R657,
  R658: codes.R658,
  R659: codes.R659,
  R660: codes.R660,
  R661: codes.R661,
  R662: codes.R662,
  R663: codes.R663,
  R664: codes.R664,
  R665: codes.R665,
  R666: codes.R666,
  R667: codes.R667,
  R668: codes.R668,
  R669: codes.R669,
  R670: codes.R670,
  R671: codes.R671,
  R711: codes.R711,
  R712: codes.R712,
  R713: codes.R713,
  R723: codes.R723,
  R724: codes.R724,
  R725: codes.R725,
  R726: codes.R726,
} satisfies Record<string, SocureFinding>;

const synthetic = {
  I202: codes.I202,
  I203: codes.I203,
  I204: codes.I204,
  I205: codes.I205,
  I206: codes.I206,
  I207: codes.I207,
  I208: codes.I208,
  I209: codes.I209,
  I210: codes.I210,
  I211: codes.I211,
  I212: codes.I212,
  I213: codes.I213,
  I214: codes.I214,
  I215: codes.I215,
  I554: codes.I554,
  I556: codes.I556,
  I557: codes.I557,
  I558: codes.I558,
  I559: codes.I559,
  I569: codes.I569,
  I570: codes.I570,
  I610: codes.I610,
  I618: codes.I618,
  I621: codes.I621,
  I622: codes.I622,
  I623: codes.I623,
  I624: codes.I624,
  I640: codes.I640,
  I708: codes.I708,
  I709: codes.I709,
  I710: codes.I710,
  I718: codes.I718,
  I719: codes.I719,
  I912: codes.I912,
  R201: codes.R201,
  R202: codes.R202,
  R203: codes.R203,
  R204: codes.R204,
  R205: codes.R205,
  R206: codes.R206,
  R207: codes.R207,
  R209: codes.R209,
  R212: codes.R212,
  R213: codes.R213,
  R214: codes.R214,
  R215: codes.R215,
  R216: codes.R216,
  R217: codes.R217,
  R218: codes.R218,
  R219: codes.R219,
  R220: codes.R220,
  R221: codes.R221,
  R222: codes.R222,
  R223: codes.R223,
  R224: codes.R224,
  R225: codes.R225,
  R226: codes.R226,
  R227: codes.R227,
  R228: codes.R228,
  R229: codes.R229,
  R230: codes.R230,
  R231: codes.R231,
  R232: codes.R232,
  R233: codes.R233,
  R234: codes.R234,
  R235: codes.R235,
  R236: codes.R236,
  R237: codes.R237,
  R238: codes.R238,
  R239: codes.R239,
  R240: codes.R240,
  R241: codes.R241,
  R242: codes.R242,
  R243: codes.R243,
  R244: codes.R244,
  R298: codes.R298,
  R299: codes.R299,
  R551: codes.R551,
  R557: codes.R557,
  R559: codes.R559,
  R563: codes.R563,
  R564: codes.R564,
  R573: codes.R573,
  R574: codes.R574,
  R601: codes.R601,
  R602: codes.R602,
  R603: codes.R603,
  R606: codes.R606,
  R607: codes.R607,
  R608: codes.R608,
  R610: codes.R610,
  R615: codes.R615,
  R619: codes.R619,
  R659: codes.R659,
  R660: codes.R660,
  R661: codes.R661,
  R662: codes.R662,
  R663: codes.R663,
  R701: codes.R701,
  R703: codes.R703,
  R704: codes.R704,
  R705: codes.R705,
  R901: codes.R901,
  R922: codes.R922,
  R940: codes.R940,
} satisfies Record<string, SocureFinding>;

const negPosList = {
  I140: codes.I140,
  I141: codes.I141,
  I142: codes.I142,
  I143: codes.I143,
  I144: codes.I144,
  I145: codes.I145,
  I146: codes.I146,
  R140: codes.R140,
  R141: codes.R141,
  R142: codes.R142,
  R143: codes.R143,
  R144: codes.R144,
  R145: codes.R145,
  R146: codes.R146,
  R150: codes.R150,
  R160: codes.R160,
  R161: codes.R161,
  R162: codes.R162,
  R163: codes.R163,
  R164: codes.R164,
  R165: codes.R165,
  R166: codes.R166,
} satisfies Record<string, SocureFinding>;

// Keyed by property they correspond to - some Socure modules might be missing
export const SOCURE_REASON_CODES = {
  all: codes,
  accountIntelligence,
  addressRisk,
  nameAddressCorrelation,
  alertList,
  ecbsv,
  emailRisk,
  nameEmailCorrelation,
  globalWatchlist,
  watchlistStandard,
  watchlistPlus,
  watchlistPremier,
  kyc,
  kycPlus,
  optionalCodes,
  deceasedCheck,
  phoneRisk,
  namePhoneCorrelation,
  documentVerification,
  deviceRisk,
  deviceIdentityCorrelation,
  firstPartyFraud,
  fraud,
  synthetic,
  negPosList,
};

const weightByLevel = {
  success: 3,
  warning: 2,
  info: 1,
};
export const extractSocureFindings = ({
  reasonCodes,
  modules,
}: {
  reasonCodes?: string[] | null;
  modules: (keyof typeof SOCURE_REASON_CODES)[];
}) => {
  const uniq: Record<string, boolean> = {};
  const socureFindings = (reasonCodes || [])
    .map((reasonCode) => {
      const socureFinding = modules
        .map((module) => {
          const moduleCodes = SOCURE_REASON_CODES[module] as Record<
            string,
            SocureFinding
          >;
          return moduleCodes[reasonCode];
        })
        .find(Boolean);
      return socureFinding;
    })
    .filter((socureFinding) => {
      if (!socureFinding) return false;
      if (uniq[socureFinding.code]) return false;
      uniq[socureFinding.code] = true;
      return true;
    })
    .sort((a, b) => {
      const weightA = weightByLevel[a?.level ?? 'info'];
      const weightB = weightByLevel[b?.level ?? 'info'];
      const diff = weightB - weightA;
      if (diff !== 0) return diff;
      return (b?.description ?? '').localeCompare(a?.description ?? '');
    });

  return socureFindings as SocureFinding[];
};
